import { formatPesoAmount } from '../utils/money';
import ClientPricingCategoryMultiSelect from '../components/clientPricing/ClientPricingCategoryMultiSelect';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DocumentHeader from '../components/DocumentHeader';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import CartPanel, { computeCartNonDiscountableSubtotal } from '../components/CartPanel';
import PendingQuotationTabs from '../components/PendingQuotationTabs';
import CategoryFilterBar from '../components/CategoryFilterBar';
import ProductGrid from '../components/ProductGrid';
import PosProductCard from '../components/PosProductCard';
import ProductSearchInput from '../components/ProductSearchInput';
import ProductViewToggle from '../components/ProductViewToggle';
import Modal from '../components/Modal';
import DiscountModal from '../components/DiscountModal';
import VarietyDetailModal from '../components/VarietyDetailModal';
import ModalQuotationClientPicker from '../components/ModalQuotationClientPicker';
import QuotationSummaryModal from '../components/QuotationSummaryModal';
import LoadingOverlay from '../components/LoadingOverlay';
import OrderDiscountModal from '../components/OrderDiscountModal';
import TaxExclusionModal from '../components/TaxExclusionModal';
import InlineLoading from '../components/InlineLoading';
import VarietyGroupTabs from '../components/VarietyGroupTabs';
import useCurrentUser from '../hooks/useCurrentUser';
import useProductViewPreference from '../hooks/useProductViewPreference';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useQuotationSaleConversion from '../hooks/useQuotationSaleConversion';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import useProductEvents from '../hooks/useProductEvents';
import useCatalogRefreshState from '../hooks/useCatalogRefreshState';
import useDebouncedCallback from '../hooks/useDebouncedCallback';
import usePosNavbarCart from '../hooks/usePosNavbarCart';
import usePromotionClock from '../hooks/usePromotionClock';
import {
    decodeVariableBarcode,
    resolveBarcodeMode,
    buildScaleBarcodeLookupKeys,
    buildShortBarcodeLookupKeys,
} from '../utils/barcode';
import { getBarcodePrefixMappings } from '../utils/barcodeSettings';
import { normalizeClientType, resolvePriceClientType } from '../utils/clientType';
import useProductFormatOptions from '../hooks/useProductFormatOptions';
import { hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import { mergeProductMatrix } from '../utils/productMatrix';
import {
    CATALOG_REFRESH_SUPERSEDED,
    loadCatalogForInitialization,
    loadDocumentAndCatalogForInitialization,
} from '../utils/catalogInitialization';
import { computeFormatOfferSubtotal, expandProductsWithFormatOffers, restoreProductsWithOfferItems, restoreProductsWithItemPriceSnapshots, getFormatOfferId, isFormatOfferPrice, isFormatOfferSelectable, getFormatOfferCartMessage, getFormatOfferLineIssue } from '../utils/formatOffers';
import { applyProductEvent } from '../utils/productEventPatch';
import { normalizeQuantity } from '../utils/quantity';
import {
    buildLineKey,
    normalizeBarcodeValue,
    orderAndIndexLineItems as orderAndIndexDocumentLineItems,
    resolveCurrentPriceIndexForItem,
    varietySupportsBarcodePriceTypeSelection as supportsBarcodePriceTypeSelection,
} from '../utils/documentLineItems';
import {
    MAX_PURCHASE_ITEM_QUANTITY,
    buildPurchaseItemQuantityLimitMessage,
    findInvalidPurchaseItemQuantity,
} from '../utils/purchaseItemLimits';
import {
    isResolvedProductSoldOut,
    isResolvedVarietySoldOut,
} from '../utils/productAvailability';
import { computeTaxAdjustedTotals } from '../utils/taxTotals';
import { FiInfo, FiPercent, FiSearch, FiTag, FiX } from 'react-icons/fi';
import {
  getProducts,
  createQuotation,
  updateQuotation,
  getClientById,
  getClientPricingDiscounts,
  getClientPricingCategories,
  previewClientPricingSelection,
  sendQuotation,
  duplicateQuotation,
  getQuotationById,
  getCategories,
  getBranchTaxes,
} from '../api';
import {
    applyPromotionsToLineItems,
    buildPromotionLineItem,
    computeLineItemsNonDiscountableSubtotal,
    computeLineItemsSubtotal,
    computeOrderTotalsFromSubtotal,
} from '../utils/promotionPricing';
import { collectCatalogPromotions } from '../utils/priceListPromotions';
import { compactQuotationItemsPayload } from '../utils/documentItemPayload';
import { applyClientPricingDiscountsToMatrices } from '../utils/clientPricingDiscounts';
import { isImmutableQuotationStatus } from '../utils/quotationStatus';
import { resolveQuotationClientPricingIntent } from '../utils/quotationPricingIntent';
import { ALL_VARIETY_GROUP_ID, filterVarietiesByGroup } from '../utils/varietyGroups';
import { getProductSearchMatch, tokenizeProductSearchQuery } from '../utils/productSearch';

const QuotationBuilderPage = () => {
    const productFormatOptions = useProductFormatOptions();
    const [searchParams, setSearchParams] = useSearchParams();
    const [modalVariety, setModalVariety] = useState(null);
    const [modalVarietyPrice, setModalVarietyPrice] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [categoriesError, setCategoriesError] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchVarietyResults, setSearchVarietyResults] = useState([]);
    const [suggestedSearchVarietyResults, setSuggestedSearchVarietyResults] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [totalAmount, setTotalPrice] = useState(0);
    const [showQuotationModal, setShowQuotationModal] = useState(false);
    const [quotationDetails, setQuotationDetails] = useState(null);
    const [name, setName] = useState('');
    const [rut, setRut] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [phone, setPhone] = useState('');
    const [giro, setGiro] = useState('');
    const [clientId, setClientId] = useState(null);
    const [quotationId, setQuotationId] = useState(null);
    const [clientType, setClientType] = useState('detalle');
    const [modalProduct, setModalProduct] = useState(null);
    const [activeVarietyGroupId, setActiveVarietyGroupId] = useState(ALL_VARIETY_GROUP_ID);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [productView, setProductView] = useProductViewPreference({
        allowedViews: ['grid'],
    });
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const { confirm } = useConfirmDialog();
    const [isLoading, setIsLoading] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const navigate = useNavigate();
    const { branchId: routeBranchId, id: routeQuotationId } = useParams();
    const branchId = routeBranchId || localStorage.getItem('selectedBranchId');
    const { requestConversion, conversionDialog } = useQuotationSaleConversion(branchId);
    const searchInputRef = useRef(null);
    const barcodeInputRef = useRef(null);
    const hasShownConsolidationWarningRef = useRef(false);
    const refreshProductsRef = useRef(null);
    const [discounts, setDiscounts] = useState({});
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [addedOrder, setAddedOrder] = useState([]);
    const [itemsSubtotal, setItemsSubtotal] = useState(0);
    const [orderDiscountAmount, setOrderDiscountAmount] = useState(null);
    const [orderDiscountPercentage, setOrderDiscountPercentage] = useState(null);
    const [showOrderDiscountModal, setShowOrderDiscountModal] = useState(false);
    const [taxes, setTaxes] = useState([]);
    const [taxesLoading, setTaxesLoading] = useState(false);
    const [taxesError, setTaxesError] = useState(null);
    const [excludedTaxIds, setExcludedTaxIds] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [promotionClock, setPromotionClock] = useState(null);
    const [showTaxModal, setShowTaxModal] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);
    const [status, setStatus] = useState('draft');
    const [clientMessage, setClientMessage] = useState('');
    const [clientMessageType, setClientMessageType] = useState('info');
    const [showToast, setShowToast] = useState(null);
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [typeSelection, setTypeSelection] = useState('');
    const [showQuotationCard, setShowQuotationCard] = useState(false);
    const [linePriceMeta, setLinePriceMeta] = useState({});
    const [clientPricingDiscountMap, setClientPricingDiscountMap] = useState({});
    const [clientPricingCategories, setClientPricingCategories] = useState([]);
    const [clientPricingCategoriesLoading, setClientPricingCategoriesLoading] = useState(false);
    const [quoteClientPricingCategoryIds, setQuoteClientPricingCategoryIds] = useState(null);
    const [
        applyQuoteClientPricingCategoryDiscounts,
        setApplyQuoteClientPricingCategoryDiscounts,
    ] = useState(true);
    const [quoteClientPricingCategoryDirty, setQuoteClientPricingCategoryDirty] = useState(false);
    const { user } = useCurrentUser();
    const { searchMode } = useProductSearchSettings();
    const canCreateQuotation = hasPermission(user, 'quotation.create');
    const canUpdateQuotation = hasPermission(user, 'quotation.update');
    const canExcludeTaxes = hasPermission(user, 'tax.exclude');
    const canEditQuotation = quotationId ? canUpdateQuotation : canCreateQuotation;
    const canModifyQuotation = canEditQuotation && !isReadOnly;
    const canSendQuotation = canUpdateQuotation;
    const canConvertQuotation = canUpdateQuotation && status === 'sent';
    const canReadQuotation = hasPermission(user, 'quotation.read');
    const canDeleteQuotation = hasPermission(user, 'quotation.delete');
    const canReadClient = hasPermission(user, 'client.read');
    const canCreateClient = hasPermission(user, 'client.create');
    const canManageClientPricing = hasPermission(user, 'client_pricing.manage');
    const canChangeClientType = canEditQuotation;
    const canOverrideLinePriceType = hasPermission(user, 'branch.access.any');
    const hasActiveSearch = Boolean((searchTerm || '').trim());
    const mobileSearchVisible = showMobileSearch || hasActiveSearch;
    const quotationClientPricingPayload = useMemo(() => (
        canManageClientPricing
            ? {
                applyClientPricingCategoryDiscounts: applyQuoteClientPricingCategoryDiscounts,
                quoteClientPricingCategoryIds,
            }
            : {}
    ), [
        applyQuoteClientPricingCategoryDiscounts,
        canManageClientPricing,
        quoteClientPricingCategoryIds,
    ]);

    const handleSetClientId = useCallback((nextClientId) => {
        setClientId(nextClientId);
        if (!canManageClientPricing) return;
        setQuoteClientPricingCategoryDirty(false);
        setQuoteClientPricingCategoryIds(null);
        setApplyQuoteClientPricingCategoryDiscounts(true);
    }, [canManageClientPricing]);

    const closeModalVariety = () => {
        setModalVariety(null);
        setSearchTerm('');
    };

    const closeModal = () => {
        setModalVarietyPrice(null);
        setSearchTerm('');
    };

    const clientIdParam = searchParams.get('clientId') || null;
    const typeParam = searchParams.get('type') || searchParams.get('clientType') || null;
    const quotationRouteId = routeQuotationId || searchParams.get('id') || null;

    const resolveClientType = useCallback((value) => {
        const normalized = normalizeClientType(value);
        if (!normalized) return '';
        return productFormatOptions.some((option) => option.value === normalized)
            ? normalized
            : '';
    }, [productFormatOptions]);

    const resolveSaleType = useCallback(
        (value) => resolveClientType(value) || 'detalle',
        [resolveClientType]
    );

    const effectiveClientType = clientType;
    const isSellablePrice = (price) => price && price.price !== null && price.price !== undefined;
    // Invariant: one active UI line per (varietyId, priceIndex) slot.
    const lineKey = buildLineKey;
    const orderAndIndexLineItems = (items) =>
        orderAndIndexDocumentLineItems(items, addedOrder);
    const normalizePriceTypeValue = (value) => normalizeClientType(value) || '';
    const resolveDiscountSource = (source, discount = 0) => {
        const normalized = String(source || '').trim();
        if (normalized) return normalized;
        return Number(discount || 0) > 0 ? 'manual' : 'none';
    };
    const mergeDiscountSource = (currentSource, nextSource) => {
        const current = resolveDiscountSource(currentSource);
        const next = resolveDiscountSource(nextSource);
        if (current === 'client_category' || next === 'client_category') return 'client_category';
        if (current === 'promotion' || next === 'promotion') return 'promotion';
        if (current === 'manual' || next === 'manual') return 'manual';
        return next || current || 'none';
    };
    const varietySupportsBarcodePriceTypeSelection = (variety) => {
        return supportsBarcodePriceTypeSelection(variety, isSellablePrice);
    };
    const applyBarcodeAutoPriceSelectionMeta = (variety, priceIndex) => {
        if (!Number.isInteger(variety?.id) || !Number.isInteger(priceIndex) || priceIndex < 0) return;
        const key = lineKey(variety.id, priceIndex);
        const price = variety?.VarietyPrices?.[priceIndex];
        const defaultPriceType = normalizePriceTypeValue(resolvePriceClientType(price));
        const autoPriceSelection = !isFormatOfferPrice(price) && varietySupportsBarcodePriceTypeSelection(variety);
        setLinePriceMeta((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                priceType: normalizePriceTypeValue(prev[key]?.priceType) || defaultPriceType,
                priceTypeOverride: Boolean(prev[key]?.priceTypeOverride),
                autoPriceSelection,
            },
        }));
    };
    const isPriceAllowedByRegister = (price) => isSellablePrice(price);

    const isPriceAllowedForType = (type, variety, price) => {
        if (!isPriceAllowedByRegister(price) || !isFormatOfferSelectable(price)) return false;
        if (!type) return true;
        const priceType = resolvePriceClientType(price);
        return priceType === normalizeClientType(type);
    };

    const getAllowedPricesForVariety = (variety, type = effectiveClientType) =>
        (variety.VarietyPrices || []).filter((price) => isPriceAllowedForType(type, variety, price));

    const getPriceIndexForVarietyPrice = (variety, targetPrice) => {
        const prices = variety?.VarietyPrices || [];
        const targetPriceId = Number(targetPrice?.id);
        if (Number.isInteger(targetPriceId)) {
            const byId = prices.findIndex((price) => Number(price?.id) === targetPriceId);
            if (byId >= 0) return byId;
        }
        return prices.indexOf(targetPrice);
    };

    const getUnitDiscountForVarietyPrice = (variety, targetPrice) => {
        const priceIndex = getPriceIndexForVarietyPrice(variety, targetPrice);
        if (!Number.isInteger(priceIndex) || priceIndex < 0) return 0;
        const rawDiscount = Number(discounts?.[variety?.id]?.[priceIndex] || 0);
        return Number.isFinite(rawDiscount) ? rawDiscount : 0;
    };

    const varietyIsVisible = (variety, type = effectiveClientType) => {
        const allowedPrices = getAllowedPricesForVariety(variety, type);
        if (!allowedPrices.length) return false;
        return true;
    };
    const searchableProducts = useMemo(
        () => products.filter((product) =>
            (product.Varieties || []).some((variety) => varietyIsVisible(variety))
        ),
        [products, effectiveClientType]
    );

    const buildEmptyProductMatrix = (productsData) =>
        mergeProductMatrix(productsData || [], [], {}, 0);

    const mergeLoadedMatrix = (productsData, loadedMatrix) => {
        const next = buildEmptyProductMatrix(productsData);
        Object.entries(loadedMatrix || {}).forEach(([varietyId, row]) => {
            const values = Array.isArray(row) ? row : [];
            next[varietyId] = [...(next[varietyId] || [])];
            values.forEach((value, index) => {
                next[varietyId][index] = value;
            });
        });
        return next;
    };


    const resolveProductCategoryId = useCallback((product) => {
        return product?.categoryId ?? product?.Category?.id ?? null;
    }, []);
    const categoriesForDisplay = useMemo(
        () => (categories || []).filter((category) => category?.isActive !== false),
        [categories]
    );
    const categoryIdsWithProducts = useMemo(() => {
        const ids = new Set();
        (products || []).forEach((product) => {
            const id = resolveProductCategoryId(product);
            if (Number.isInteger(id)) ids.add(id);
        });
        return ids;
    }, [products, resolveProductCategoryId]);
    const displayCategories = useMemo(
        () => categoriesForDisplay.filter((category) => categoryIdsWithProducts.has(category.id)),
        [categoriesForDisplay, categoryIdsWithProducts]
    );
    const categoriesToRender = useMemo(() => {
        if (searchTerm || selectedCategory === 'all') return displayCategories;
        return displayCategories.filter(
            (category) => String(category.id) === String(selectedCategory)
        );
    }, [displayCategories, searchTerm, selectedCategory]);

    const varietyTaxProfileById = useMemo(() => {
        if (!canExcludeTaxes || !excludedTaxIds.length || !taxes.length) {
            return new Map();
        }
        const activeTaxes = (taxes || []).filter(
            (tax) => tax && tax.isActive !== false && tax.branchActive !== false
        );
        const taxesById = new Map(
            activeTaxes
                .map((tax) => [Number(tax.id), tax])
                .filter(([taxId]) => Number.isFinite(taxId))
        );
        const defaultTaxIds = activeTaxes
            .filter((tax) => tax.applyToAllProducts)
            .map((tax) => Number(tax.id))
            .filter((taxId) => Number.isFinite(taxId));
        const excludedSet = new Set(
            (excludedTaxIds || [])
                .map((taxId) => Number(taxId))
                .filter((taxId) => Number.isFinite(taxId))
        );

        const sumRate = (list) =>
            list.reduce((sum, tax) => {
                const rate = Number(tax?.rate);
                return sum + (Number.isFinite(rate) ? rate : 0);
            }, 0);

        const map = new Map();
        (products || []).forEach((product) => {
            const useDefaultTaxes = product?.useDefaultTaxes !== false;
            let taxIds = useDefaultTaxes ? defaultTaxIds : (product?.taxIds || []);
            taxIds = (taxIds || [])
                .map((taxId) => Number(taxId))
                .filter((taxId) => taxesById.has(taxId));
            if (!useDefaultTaxes && taxIds.length === 0) {
                taxIds = defaultTaxIds;
            }
            const fullTaxes = taxIds.map((taxId) => taxesById.get(taxId)).filter(Boolean);
            const appliedTaxes = excludedSet.size
                ? fullTaxes.filter((tax) => !excludedSet.has(Number(tax.id)))
                : fullTaxes;
            const fullRate = sumRate(fullTaxes) / 100;
            const remainingRate = sumRate(appliedTaxes) / 100;
            const appliesTaxExclusion = excludedSet.size
                && fullTaxes.length
                && appliedTaxes.length !== fullTaxes.length
                && fullRate > 0;

            (product?.Varieties || []).forEach((variety) => {
                if (!Number.isInteger(variety?.id)) return;
                map.set(variety.id, {
                    fullRate,
                    remainingRate,
                    appliesTaxExclusion,
                });
            });
        });

        return map;
    }, [products, taxes, excludedTaxIds, canExcludeTaxes]);

    const resolveVarietyPriceDisplay = useCallback((variety, varietyPrice, unitDiscount = 0) => {
        const basePriceRaw = Number(varietyPrice?.price);
        const basePrice = Number.isFinite(basePriceRaw) ? basePriceRaw : 0;
        const discountRaw = isFormatOfferPrice(varietyPrice) ? 0 : Number(unitDiscount);
        const resolvedDiscount = Number.isFinite(discountRaw) ? discountRaw : 0;
        const effectivePrice = basePrice - resolvedDiscount;
        const profile = varietyTaxProfileById.get(variety?.id);
        if (!profile?.appliesTaxExclusion) {
            return { price: effectivePrice, taxFreeApplied: false };
        }
        const grossValue = Math.floor(effectivePrice);
        const netFromFull = Math.round(grossValue / (1 + profile.fullRate));
        const adjustedGross = Math.round(netFromFull * (1 + profile.remainingRate));
        return { price: adjustedGross, taxFreeApplied: true };
    }, [varietyTaxProfileById]);

    const resolveCartItemPricing = useCallback((item, variety, quantityValue) => {
        const quantity = normalizeQuantity(quantityValue ?? item?.quantity ?? 0);
        const price = Number(item?.price);
        const unitPrice = Number.isFinite(price) ? price : 0;
        const discount = getFormatOfferId(item) ? 0 : Number(item?.discount);
        const unitDiscount = Number.isFinite(discount) ? discount : 0;
        const explicitSubtotal = Number(item?.subtotal);
        const hasExplicitSubtotal = Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0;
        const discountedUnitPrice = hasExplicitSubtotal && quantity > 0
            ? explicitSubtotal / quantity
            : unitPrice - unitDiscount;
        const baseGross = getFormatOfferId(item)
            ? computeFormatOfferSubtotal(unitPrice, quantity) : Math.floor(unitPrice * quantity);
        const discountedGross = hasExplicitSubtotal
            ? Math.floor(explicitSubtotal)
            : getFormatOfferId(item) ? baseGross : Math.floor(discountedUnitPrice * quantity);
        const eligibleGrossValue = Number(item?.orderDiscountEligibleSubtotal);
        const hasEligibleGross = item?.orderDiscountEligibleSubtotal !== null
            && item?.orderDiscountEligibleSubtotal !== undefined
            && item?.orderDiscountEligibleSubtotal !== '';
        const orderDiscountEligibleGross = getFormatOfferId(item)
            ? 0
            : hasEligibleGross && Number.isFinite(eligibleGrossValue)
                ? Math.min(Math.max(Math.floor(eligibleGrossValue), 0), discountedGross)
                : discountedGross;
        const varietyId = Number.isInteger(item?.varietyId) ? item.varietyId : variety?.id;
        const profile = varietyTaxProfileById.get(varietyId);
        if (!profile?.appliesTaxExclusion) {
            return {
                unitPrice,
                discountedUnitPrice,
                subtotal: discountedGross,
                orderDiscountEligibleSubtotal: orderDiscountEligibleGross,
                taxFreeApplied: false,
            };
        }

        const adjustGross = (grossValue) => {
            const netFromFull = Math.round(grossValue / (1 + profile.fullRate));
            return Math.round(netFromFull * (1 + profile.remainingRate));
        };

        const adjustedGross = adjustGross(baseGross);
        const adjustedDiscountedGross = adjustGross(discountedGross);
        const adjustedOrderDiscountEligibleGross = adjustGross(orderDiscountEligibleGross);
        return {
            unitPrice: quantity > 0 ? adjustedGross / quantity : unitPrice,
            discountedUnitPrice: quantity > 0 ? adjustedDiscountedGross / quantity : discountedUnitPrice,
            subtotal: adjustedDiscountedGross,
            orderDiscountEligibleSubtotal: adjustedOrderDiscountEligibleGross,
            taxFreeApplied: true,
        };
    }, [varietyTaxProfileById]);

    const computeTotals = (
        productsData,
        quantitiesData,
        discountsData,
        overrideOrderDiscountAmount,
        overrideOrderDiscountPercentage
    ) => {
        const resolvedProducts = Array.isArray(productsData) ? productsData : products;
        const resolvedQuantities = quantitiesData ?? quantities;
        const resolvedDiscounts = discountsData ?? discounts;
        const resolvedOrderDiscountAmount =
            overrideOrderDiscountAmount !== undefined ? overrideOrderDiscountAmount : orderDiscountAmount;
        const resolvedOrderDiscountPercentage =
            overrideOrderDiscountPercentage !== undefined
                ? overrideOrderDiscountPercentage
                : orderDiscountPercentage;

        if (canExcludeTaxes && taxes.length) {
            return computeTaxAdjustedTotals({
                products: resolvedProducts,
                quantities: resolvedQuantities,
                discounts: resolvedDiscounts,
                taxes,
                excludedTaxIds,
                orderDiscountAmount: resolvedOrderDiscountAmount,
                orderDiscountPercentage: resolvedOrderDiscountPercentage,
                isPriceAllowed: (price) => isPriceAllowedByRegister(price),
                promotions,
                promotionContext: 'quotation',
                promotionNow,
            });
        }

        const lineItems = buildItemsPayload({
            productsData: resolvedProducts,
            quantitiesData: resolvedQuantities,
            discountsData: resolvedDiscounts,
            orderDiscountAmountData: resolvedOrderDiscountAmount,
            orderDiscountPercentageData: resolvedOrderDiscountPercentage,
        });
        const subtotal = computeLineItemsSubtotal(lineItems);
        return computeOrderTotalsFromSubtotal({
            subtotal,
            nonDiscountableSubtotal: computeLineItemsNonDiscountableSubtotal(lineItems),
            orderDiscountAmount: resolvedOrderDiscountAmount,
            orderDiscountPercentage: resolvedOrderDiscountPercentage,
        });
    };

    useEffect(() => {
        if (quotationRouteId && isReadOnly) return;
        if (!canExcludeTaxes) return;
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
    }, [excludedTaxIds, taxes, promotions, isReadOnly, quotationRouteId]);

    useEffect(() => {
        if (quotationRouteId && isReadOnly) return;
        if (canExcludeTaxes) return;
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
    }, [promotions, isReadOnly, quotationRouteId]);

    const fetchEffectiveClientPricingMap = useCallback(async () => {
        if (quotationRouteId && isReadOnly) return { map: {}, clientPricing: null };
        if (!branchId || !applyQuoteClientPricingCategoryDiscounts) {
            return { map: {}, clientPricing: null };
        }
        if (canManageClientPricing && quoteClientPricingCategoryIds !== null) {
            const result = await previewClientPricingSelection(quoteClientPricingCategoryIds, { branchId });
            return { map: result.discountsByVarietyPriceId || {}, clientPricing: null };
        }
        if (!clientId) return { map: {}, clientPricing: null };
        const clientPricing = await getClientPricingDiscounts(clientId, { branchId });
        return {
            map: clientPricing?.discountsByVarietyPriceId || {},
            clientPricing,
        };
    }, [
        applyQuoteClientPricingCategoryDiscounts,
        branchId,
        canManageClientPricing,
        clientId,
        isReadOnly,
        quotationRouteId,
        quoteClientPricingCategoryIds,
    ]);

    const catalogRefreshScopeKey = [
        branchId || '',
        quotationRouteId || 'new',
        clientId || '',
        applyQuoteClientPricingCategoryDiscounts ? 'apply' : 'skip',
        canManageClientPricing ? (quoteClientPricingCategoryIds === null ? 'client' : quoteClientPricingCategoryIds.join(',')) : '',
    ].join(':');
    const {
        begin: beginCatalogRefresh,
        capture: captureCatalogRefresh,
        latest: latestCatalogState,
    } = useCatalogRefreshState(catalogRefreshScopeKey, {
        products, quantities, discounts, linePriceMeta,
        orderDiscountAmount, orderDiscountPercentage, computeTotals,
    });
    const refreshProducts = useCallback(async () => {
        if (!branchId) return;
        const request = beginCatalogRefresh(catalogRefreshScopeKey);
        if (!request) return CATALOG_REFRESH_SUPERSEDED;
        try {
            const [data, effectivePricing] = await Promise.all([
                getProducts(branchId, { pageSize: 0 }),
                fetchEffectiveClientPricingMap(),
            ]);
            const latest = latestCatalogState(request);
            if (!latest) return CATALOG_REFRESH_SUPERSEDED;
            setPromotionClock(data?.promotionClock || null);
            const normalized = normalizePaginatedResponse(data);
            const productsData = expandProductsWithFormatOffers(normalized.data || [], latest.products);
            const nextQuantities = mergeProductMatrix(productsData, latest.products, latest.quantities, 0);
            const mergedDiscounts = mergeProductMatrix(productsData, latest.products, latest.discounts, 0);
            const pricingResult = applyClientPricingDiscountsToMatrices({
                products: productsData,
                discounts: mergedDiscounts,
                linePriceMeta: latest.linePriceMeta,
                discountsByVarietyPriceId: effectivePricing.map,
            });
            const nextDiscounts = pricingResult.discounts;

            setProducts(productsData);
            setFilteredProducts(productsData);
            setPromotions(collectCatalogPromotions(productsData));
            setQuantities(nextQuantities);
            setDiscounts(nextDiscounts);
            setLinePriceMeta(pricingResult.linePriceMeta);
            setClientPricingDiscountMap(effectivePricing.map);



            const totals = latest.computeTotals(
                productsData,
                nextQuantities,
                nextDiscounts,
                latest.orderDiscountAmount,
                latest.orderDiscountPercentage
            );
            setItemsSubtotal(totals.subtotal);
            setTotalPrice(totals.total);
            return productsData;
        } catch (error) {
            if (!latestCatalogState(request)) return CATALOG_REFRESH_SUPERSEDED;
            if (error.response?.status === 401) {
                navigate('/');
            }
            throw error;
        }
    }, [
        branchId,
        catalogRefreshScopeKey,
        products,
        quantities,
        discounts,
        orderDiscountAmount,
        orderDiscountPercentage,
        promotions,
        canManageClientPricing,
        quoteClientPricingCategoryDirty,
        fetchEffectiveClientPricingMap,
        navigate,
        beginCatalogRefresh,
        latestCatalogState,
    ]);
    refreshProductsRef.current = refreshProducts;

    const { schedule: scheduleCatalogRefresh } = useDebouncedCallback(() => {
        refreshProducts().catch((error) => {
            console.error('Error refreshing quotation products:', error);
            setShowToast({ type: 'error', text: 'No se pudieron actualizar los productos. Recarga antes de continuar.' });
        });
    }, 250);

    useEffect(() => {
        setPromotions([]);
        setPromotionClock(null);
    }, [branchId, quotationRouteId]);

    const promotionBoundaries = useMemo(() => (
        promotionClock?.nextBoundaryAt
            ? [...promotions, { startsAt: promotionClock.nextBoundaryAt }]
            : promotions
    ), [promotions, promotionClock?.nextBoundaryAt]);
    const promotionNow = usePromotionClock(promotionBoundaries, () => {
        refreshProducts();
    }, promotionClock);

    const handleProductEvent = useCallback((event) => {
        if (quotationId && isImmutableQuotationStatus(status)) return;
        const patchedProducts = applyProductEvent(products, event);
        const nextProducts = patchedProducts && expandProductsWithFormatOffers(patchedProducts, products);
        if (!nextProducts) {
            scheduleCatalogRefresh();
            return;
        }
        if (nextProducts === products) return;
        const nextQuantities = mergeProductMatrix(nextProducts, products, quantities, 0);
        const mergedDiscounts = mergeProductMatrix(nextProducts, products, discounts, 0);
        const pricingResult = applyClientPricingDiscountsToMatrices({
            products: nextProducts,
            discounts: mergedDiscounts,
            linePriceMeta,
            discountsByVarietyPriceId: clientPricingDiscountMap,
        });
        const nextDiscounts = pricingResult.discounts;

        setProducts(nextProducts);
        setPromotions(collectCatalogPromotions(nextProducts));
        setQuantities(nextQuantities);
        setDiscounts(nextDiscounts);
        setLinePriceMeta(pricingResult.linePriceMeta);

        const totals = computeTotals(
            nextProducts,
            nextQuantities,
            nextDiscounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
    }, [
        products,
        quantities,
        discounts,
        orderDiscountAmount,
        orderDiscountPercentage,
        promotions,
        linePriceMeta,
        clientPricingDiscountMap,
        scheduleCatalogRefresh,
        quotationId,
        status,
    ]);

    useProductEvents(quotationRouteId && isReadOnly ? null : branchId, handleProductEvent);

    useEffect(() => {
        let mounted = true;
        if (quotationRouteId) setIsReadOnly(true);
        const loadQuotationIfNeeded = async () => {
            const idParam = quotationRouteId;
            if (!idParam) return;
            try {
                const initialized = await loadDocumentAndCatalogForInitialization(
                    () => getQuotationById(branchId, idParam),
                    () => refreshProductsRef.current?.(),
                    () => mounted && Boolean(branchId)
                );
                if (!mounted) return;
                if (!initialized) throw new Error('No se pudo cargar el catálogo vigente.');
                const { document: data } = initialized;
                const loadedStatus = data.status || 'draft';
                // Resolve the document first, then request the newest catalog using
                // the current scoped callback. No await remains between this winning
                // catalog and the synchronous quotation hydration below.
                let productsForMatrix = initialized.catalog;
                productsForMatrix = isImmutableQuotationStatus(loadedStatus)
                    ? restoreProductsWithItemPriceSnapshots(productsForMatrix, data.items || [])
                    : restoreProductsWithOfferItems(productsForMatrix, data.items || []);
                setProducts(productsForMatrix);
                setFilteredProducts(productsForMatrix);
                if (isImmutableQuotationStatus(loadedStatus)) {
                    setPromotions([]);
                    setPromotionClock(null);
                } else {
                    setPromotions(collectCatalogPromotions(productsForMatrix));
                }
                setQuotationId(data.id);
                setStatus(loadedStatus);
                setIsReadOnly(isImmutableQuotationStatus(loadedStatus));
                const resolvedClientId = data.clientId || data.Client?.id || null;
                setClientId(resolvedClientId);
                setName(data.Client?.name || '');
                setEmail(data.Client?.email || '');
                setRut(data.Client?.rut || '');
                setPhone(data.Client?.phone || '');
                setAddress(data.Client?.address || '');
                setCity(data.Client?.city || '');
                setGiro(data.Client?.giro || '');
                if (resolvedClientId) {
                    setShowQuotationCard(false);
                }
                const loadedQuantities = {};
                const loadedDiscounts = {};
                const loadedLineMeta = {};
                const consolidatedBySlot = new Map();
                const varietyById = new Map();
                (Array.isArray(productsForMatrix) ? productsForMatrix : []).forEach((product) => {
                    (product?.Varieties || []).forEach((variety) => {
                        if (Number.isInteger(Number(variety?.id))) {
                            varietyById.set(Number(variety.id), variety);
                        }
                    });
                });
                const loadedPriceTypes = new Set();
                const loadedItems = [...(data.items || [])].sort((a, b) => {
                    const lineA = Number(a?.lineIndex);
                    const lineB = Number(b?.lineIndex);
                    if (Number.isInteger(lineA) && Number.isInteger(lineB)) return lineA - lineB;
                    if (Number.isInteger(lineA)) return -1;
                    if (Number.isInteger(lineB)) return 1;
                    return 0;
                });
                let consolidatedLines = 0;

                loadedItems.forEach((item) => {
                    const varietyId = Number(item?.varietyId);
                    if (!Number.isInteger(varietyId)) return;
                    const variety = varietyById.get(varietyId);
                    const priceIndex = resolveCurrentPriceIndexForItem(item, variety);
                    const key = lineKey(varietyId, priceIndex);
                    const quantity = normalizeQuantity(item?.quantity || 0);
                    const discount = Number(item?.discount || 0);
                    const discountSource = resolveDiscountSource(item?.discountSource, discount);
                    const priceType = normalizePriceTypeValue(item?.priceType);
                    if (priceType) loadedPriceTypes.add(priceType);
                    const unitPrice = Number(item?.price || 0) || 0;
                    const varietyPriceId = Number(item?.varietyPriceId);

                    if (!consolidatedBySlot.has(key)) {
                        consolidatedBySlot.set(key, {
                            varietyId,
                            priceIndex,
                            varietyPriceId: Number.isInteger(varietyPriceId) ? varietyPriceId : null,
                            quantity,
                            discount,
                            discountSource,
                            manualDiscountCandidate: Number(
                                item?.manualDiscount ?? (discountSource === 'manual' ? discount : 0)
                            ) || null,
                            clientPricingCategoryId: item?.clientPricingCategoryId || null,
                            clientPricingCategoryName: item?.clientPricingCategoryName || null,
                            clientPricingDiscountType: item?.clientPricingDiscountType || null,
                            clientPricingDiscountValue: item?.clientPricingDiscountValue ?? null,
                            priceType,
                            priceTypeOverride: Boolean(item?.priceTypeOverride),
                            autoPriceSelection: Boolean(item?.autoPriceSelection),
                            unitPrice,
                        });
                        return;
                    }

                    consolidatedLines += 1;
                    const current = consolidatedBySlot.get(key);
                    const currentQuantity = normalizeQuantity(current.quantity || 0);
                    const nextQuantity = normalizeQuantity(currentQuantity + quantity);
                    const currentUnitPrice = Number(current.unitPrice || unitPrice) || 0;
                    const currentEffectiveSubtotal = (currentUnitPrice - Number(current.discount || 0)) * currentQuantity;
                    const incomingEffectiveSubtotal = (unitPrice - discount) * quantity;
                    const mergedEffectiveSubtotal = currentEffectiveSubtotal + incomingEffectiveSubtotal;
                    let mergedDiscount = 0;
                    if (nextQuantity > 0) {
                        const mergedEffectiveUnit = mergedEffectiveSubtotal / nextQuantity;
                        mergedDiscount = Math.round(currentUnitPrice - mergedEffectiveUnit);
                        if (!Number.isFinite(mergedDiscount)) mergedDiscount = 0;
                        mergedDiscount = Math.min(Math.max(mergedDiscount, 0), currentUnitPrice);
                    }

                    current.quantity = nextQuantity;
                    current.discount = mergedDiscount;
                    current.discountSource = mergeDiscountSource(
                        current.discountSource,
                        discountSource
                    );
                    if (!current.clientPricingCategoryId && item?.clientPricingCategoryId) {
                        current.clientPricingCategoryId = item.clientPricingCategoryId;
                    }
                    if (!current.clientPricingCategoryName && item?.clientPricingCategoryName) {
                        current.clientPricingCategoryName = item.clientPricingCategoryName;
                    }
                    if (!current.clientPricingDiscountType && item?.clientPricingDiscountType) {
                        current.clientPricingDiscountType = item.clientPricingDiscountType;
                    }
                    if (current.clientPricingDiscountValue == null && item?.clientPricingDiscountValue != null) {
                        current.clientPricingDiscountValue = item.clientPricingDiscountValue;
                    }
                    current.priceType = current.priceType || priceType;
                    current.priceTypeOverride = current.priceTypeOverride || Boolean(item?.priceTypeOverride);
                    current.autoPriceSelection = current.autoPriceSelection || Boolean(item?.autoPriceSelection);
                    consolidatedBySlot.set(key, current);
                });

                consolidatedBySlot.forEach((entry, key) => {
                    if (!loadedQuantities[entry.varietyId]) loadedQuantities[entry.varietyId] = [];
                    if (!loadedDiscounts[entry.varietyId]) loadedDiscounts[entry.varietyId] = [];
                    loadedQuantities[entry.varietyId][entry.priceIndex] = entry.quantity;
                    loadedDiscounts[entry.varietyId][entry.priceIndex] = entry.discount;
                    loadedLineMeta[key] = {
                        priceType: entry.priceType,
                        priceTypeOverride: Boolean(entry.priceTypeOverride),
                        autoPriceSelection: Boolean(entry.autoPriceSelection),
                        discountSource: resolveDiscountSource(entry.discountSource, entry.discount),
                        manualDiscountCandidate: entry.manualDiscountCandidate || null,
                        clientPricingCategoryId: entry.clientPricingCategoryId || null,
                        clientPricingCategoryName: entry.clientPricingCategoryName || null,
                        clientPricingDiscountType: entry.clientPricingDiscountType || null,
                        clientPricingDiscountValue: entry.clientPricingDiscountValue ?? null,
                    };
                });

                if (consolidatedLines > 0 && !hasShownConsolidationWarningRef.current) {
                    hasShownConsolidationWarningRef.current = true;
                    setShowToast({
                        type: 'error',
                        text: 'Se consolidaron líneas históricas duplicadas en esta cotización. Revisa descuentos y cantidades.',
                    });
                }
                setQuantities(mergeLoadedMatrix(productsForMatrix, loadedQuantities));
                setDiscounts(mergeLoadedMatrix(productsForMatrix, loadedDiscounts));
                setLinePriceMeta(loadedLineMeta);
                setAddedOrder(Array.from(consolidatedBySlot.values()).map((entry) => ({
                    varietyId: entry.varietyId,
                    priceIndex: entry.priceIndex,
                    ...(Number.isInteger(entry.varietyPriceId) ? { varietyPriceId: entry.varietyPriceId } : {}),
                })));
                if (loadedPriceTypes.size === 1) {
                    const [loadedType] = Array.from(loadedPriceTypes);
                    setClientType(loadedType);
                    setTypeSelection(loadedType);
                }
                if (canManageClientPricing) {
                    const loadedPricingIntent = resolveQuotationClientPricingIntent(loadedItems, data);
                    setApplyQuoteClientPricingCategoryDiscounts(
                        loadedPricingIntent.applyClientPricingCategoryDiscounts
                    );
                    setQuoteClientPricingCategoryDirty(true);
                    setQuoteClientPricingCategoryIds(
                        loadedPricingIntent.quoteClientPricingCategoryIds !== undefined
                            ? loadedPricingIntent.quoteClientPricingCategoryIds
                            : loadedPricingIntent.quoteClientPricingCategoryId != null ? [loadedPricingIntent.quoteClientPricingCategoryId] : null
                    );
                }
                setItemsSubtotal(data.itemsSubtotal || 0);
                const storedOrderDiscountAmount = Number(data.orderDiscountAmount);
                const storedOrderDiscountPercentage = Number(data.orderDiscountPercentage);
                setOrderDiscountAmount(
                    data.orderDiscountAmount !== null
                        && data.orderDiscountAmount !== undefined
                        && Number.isFinite(storedOrderDiscountAmount)
                        ? storedOrderDiscountAmount
                        : null
                );
                setOrderDiscountPercentage(
                    data.orderDiscountPercentage !== null
                        && data.orderDiscountPercentage !== undefined
                        && Number.isFinite(storedOrderDiscountPercentage)
                        ? storedOrderDiscountPercentage
                        : null
                );
                setExcludedTaxIds(Array.isArray(data.excludedTaxIds) ? data.excludedTaxIds : []);
                setTotalPrice(data.totalAmount || 0);
            } catch (error) {
                console.error(error);
                if (error.response?.status === 401) {
                    navigate('/');
                }
            }
        };

        const getClientInfoById = async () => {
            try {
                if (clientIdParam) {
                    if (!canReadClient) return;
                    const data = await getClientById(clientIdParam);
                    setAddress(data.address)
                    setCity(data.city)
                    setName(data.name)
                    handleSetClientId(data.id)
                    setPhone(data.phone)
                    setEmail(data.email)
                    setRut(data.rut)
                    setGiro(data.giro || '')
                    setShowQuotationCard(false);
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    navigate('/');
                }
            }
        }

        const loadCatalogAndQuotation = async () => {
            if (quotationRouteId) {
                await loadQuotationIfNeeded();
                return;
            }
            const productsData = await loadCatalogForInitialization(
                () => refreshProductsRef.current?.(),
                () => mounted && Boolean(branchId)
            );
            if (mounted && !productsData && branchId) {
                setShowToast({ type: 'error', text: 'La lista cambió durante la carga. Recarga la página para continuar.' });
            }
        };
        loadCatalogAndQuotation().catch((error) => {
            if (mounted) {
                console.error('Error loading quotation catalog:', error);
                setShowToast({ type: 'error', text: 'No se pudo cargar el catálogo. Recarga la página para continuar.' });
            }
        });
        getClientInfoById();
        return () => { mounted = false; };
    }, [
        navigate,
        branchId,
        clientIdParam,
        typeParam,
        canReadClient,
        canManageClientPricing,
        handleSetClientId,
        quotationRouteId,
    ]);

    useEffect(() => {
        if (!canManageClientPricing) {
            setClientPricingCategories([]);
            setClientPricingCategoriesLoading(false);
            return;
        }
        let mounted = true;
        const loadClientPricingCategories = async () => {
            setClientPricingCategoriesLoading(true);
            try {
                const data = await getClientPricingCategories({ activeOnly: true });
                if (!mounted) return;
                setClientPricingCategories(Array.isArray(data?.categories) ? data.categories : []);
            } catch (error) {
                if (mounted) setClientPricingCategories([]);
            } finally {
                if (mounted) setClientPricingCategoriesLoading(false);
            }
        };
        loadClientPricingCategories();
        return () => {
            mounted = false;
        };
    }, [canManageClientPricing]);

    useEffect(() => {
        if (quotationRouteId && isReadOnly) {
            setClientPricingDiscountMap({});
            return undefined;
        }
        let mounted = true;
        const request = captureCatalogRefresh(catalogRefreshScopeKey);
        if (!request) return undefined;
        const loadClientPricingDiscounts = async () => {
            try {
                const data = await fetchEffectiveClientPricingMap();
                if (!mounted || !latestCatalogState(request)) return;
                setClientPricingDiscountMap(data.map);

            } catch (error) {
                if (mounted && latestCatalogState(request)) setClientPricingDiscountMap({});
            }
        };
        loadClientPricingDiscounts();
        return () => {
            mounted = false;
        };
    }, [
        canManageClientPricing,
        catalogRefreshScopeKey,
        captureCatalogRefresh,
        fetchEffectiveClientPricingMap,
        isReadOnly,
        latestCatalogState,
        quotationRouteId,
        quoteClientPricingCategoryDirty,
    ]);

    useEffect(() => {
        if (quotationRouteId && isReadOnly) return;
        const result = applyClientPricingDiscountsToMatrices({
            products,
            discounts,
            linePriceMeta,
            discountsByVarietyPriceId: clientPricingDiscountMap,
        });
        if (!result.changed) return;
        setDiscounts(result.discounts);
        setLinePriceMeta(result.linePriceMeta);
        calculateTotal(quantities, result.discounts);
    }, [
        products,
        quantities,
        discounts,
        linePriceMeta,
        clientPricingDiscountMap,
        isReadOnly,
        quotationRouteId,
    ]);

    useEffect(() => {
        const fetchCategories = async () => {
            if (!branchId) return;
            setCategoriesLoading(true);
            setCategoriesError(null);
            try {
                const data = await getCategories({ includeAvailability: true, branchId });
                setCategories(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setCategories([]);
                setCategoriesError('No se pudieron cargar las categorías.');
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, [branchId]);

    useEffect(() => {
        if (!branchId || !canExcludeTaxes) {
            setTaxes([]);
            setTaxesLoading(false);
            setTaxesError(null);
            setExcludedTaxIds([]);
            return;
        }
        let isMounted = true;
        const loadTaxes = async () => {
            setTaxesLoading(true);
            setTaxesError(null);
            try {
                const data = await getBranchTaxes(branchId);
                if (!isMounted) return;
                const normalized = Array.isArray(data) ? data : [];
                const active = normalized.filter(
                    (tax) => tax && tax.isActive !== false && tax.branchActive !== false
                );
                setTaxes(active);
                const activeIds = new Set(active.map((tax) => Number(tax.id)));
                setExcludedTaxIds((prev) =>
                    (prev || []).filter((id) => activeIds.has(Number(id)))
                );
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching taxes:', error);
                setTaxes([]);
                setTaxesError('No se pudieron cargar los impuestos.');
            } finally {
                if (!isMounted) return;
                setTaxesLoading(false);
            }
        };
        loadTaxes();
        return () => {
            isMounted = false;
        };
    }, [branchId, canExcludeTaxes]);

    useEffect(() => {
        if (selectedCategory === 'all') return;
        const isStillValid = displayCategories.some(
            (category) => String(category.id) === String(selectedCategory)
        );
        if (!isStillValid) {
            setSelectedCategory('all');
        }
    }, [displayCategories, selectedCategory]);

    useEffect(() => {
        const timer = setTimeout(() => {
            barcodeInputRef.current?.focus();
            barcodeInputRef.current?.select();
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const applySearch = (nextValue) => {
        const value = (nextValue || '').toLowerCase();
        setSearchTerm(value);
        filterProducts(value, selectedCategory);
    };

    const handleSearch = (e) => {
        applySearch(e.target.value);
    };

    useEffect(() => {
        if (hasActiveSearch) {
            setShowMobileSearch(true);
        }
    }, [hasActiveSearch]);

    useEffect(() => {
        setActiveVarietyGroupId(ALL_VARIETY_GROUP_ID);
    }, [modalProduct?.id]);

    const toggleMobileSearch = () => {
        if (mobileSearchVisible && hasActiveSearch) return;
        setShowMobileSearch((prev) => {
            const next = !prev;
            if (next) {
                setTimeout(() => {
                    searchInputRef.current?.focus();
                    searchInputRef.current?.select();
                }, 0);
            }
            return next;
        });
    };

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        filterProducts(searchTerm, category);
    };

    const filterProducts = (search, category, type = effectiveClientType) => {
        let filtered = products.filter((product) =>
            (product.Varieties || []).some((variety) => varietyIsVisible(variety, type))
        );
        let varietyResults = [];
        let suggestedVarietyResults = [];

        const searchTokens = tokenizeProductSearchQuery(search);

        if (searchTokens.length) {
            const seenVarietyKeys = new Set();
            const searchEntries = products
                .map((product) => {
                    const visibleVarieties = (product.Varieties || []).filter((variety) =>
                        varietyIsVisible(variety, type)
                    );
                    if (!visibleVarieties.length) return null;
                    const match = getProductSearchMatch(product, searchTokens, {
                        varieties: visibleVarieties,
                        searchMode,
                    });
                    return match.matches ? { product, match } : null;
                })
                .filter(Boolean);

            const exactEntries = searchEntries.filter(({ match }) => match.exactMatches);
            const suggestedEntries = searchEntries.filter(({ match }) =>
                !match.exactMatches && match.suggestedMatches
            );

            filtered = exactEntries.map(({ product }) => product);
            varietyResults = exactEntries.flatMap(({ product, match }) =>
                match.matchingVarieties
                    .map((variety) => ({ product, variety }))
                    .filter((entry) => {
                        const key = `${entry.product?.id || 'x'}-${entry.variety?.id || 'x'}`;
                        if (seenVarietyKeys.has(key)) return false;
                        seenVarietyKeys.add(key);
                        return true;
                    })
            );
            suggestedVarietyResults = suggestedEntries.flatMap(({ product, match }) =>
                match.suggestedMatchingVarieties
                    .map((variety) => ({ product, variety }))
                    .filter((entry) => {
                        const key = `${entry.product?.id || 'x'}-${entry.variety?.id || 'x'}`;
                        if (seenVarietyKeys.has(key)) return false;
                        seenVarietyKeys.add(key);
                        return true;
                    })
            );

        } else if (category !== 'all') {
            filtered = products.filter(
                (product) => String(resolveProductCategoryId(product)) === String(category)
            );
        }

        // 🚀 NEW FILTER: Remove products where all varieties are disallowed for the selected type
        filtered = filtered.filter(product =>
            product.Varieties.some(variety => varietyIsVisible(variety, type))
        );

        setFilteredProducts(filtered);
        setSearchVarietyResults(varietyResults);
        setSuggestedSearchVarietyResults(suggestedVarietyResults);
    };

    const findVarietyPriceByBarcode = (lookupKeys) => {
        if (!Array.isArray(lookupKeys) || lookupKeys.length === 0) return null;
        const keySet = new Set(lookupKeys.filter(Boolean));

        for (const product of products) {
            for (const variety of product.Varieties || []) {
                for (let index = 0; index < (variety.VarietyPrices || []).length; index += 1) {
                    const price = variety.VarietyPrices[index];
                    const barcode = price?.barcode == null ? null : String(price.barcode).trim();
                    if (barcode && keySet.has(barcode)) {
                        return { variety, varietyPrice: price, priceIndex: index };
                    }
                }
            }
        }

        return null;
    };

    const findAnyVarietyPriceByBarcode = (lookupKeys) => {
        if (!Array.isArray(lookupKeys) || lookupKeys.length === 0) return null;
        const keySet = new Set(lookupKeys.filter(Boolean));

        for (const product of products) {
            for (const variety of product.Varieties || []) {
                for (let index = 0; index < (variety.VarietyPrices || []).length; index += 1) {
                    const price = variety.VarietyPrices[index];
                    if (!isPriceAllowedByRegister(price)) continue;
                    const barcode = price?.barcode == null ? null : String(price.barcode).trim();
                    if (barcode && keySet.has(barcode)) {
                        return { variety, varietyPrice: price, priceIndex: index };
                    }
                }
            }
        }

        return null;
    };

    const handleQuickEntryKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (isReadOnly) {
            setShowToast({ type: 'info', text: 'Cotización en solo lectura: duplica para editar.' });
            return;
        }
        const trimmed = (searchTerm || '').trim();
        if (!trimmed || !/^\d+$/.test(trimmed)) return;

        let match = null;
        let anyMatch = null;

        if (trimmed.length === 13) {
            const mappings = getBarcodePrefixMappings();
            const mode = resolveBarcodeMode(trimmed, mappings);
            if (mode) {
                const decoded = decodeVariableBarcode(trimmed, mode);
                if (decoded.isValid && decoded.isScale) {
                    const keys = buildScaleBarcodeLookupKeys(decoded);
                    match = findVarietyPriceByBarcode(keys);
                    if (!match) {
                        anyMatch = findAnyVarietyPriceByBarcode(keys);
                    }
                    const targetMatch = match || anyMatch;
                    if (targetMatch) {
                        if (!match && anyMatch && clientType) {
                            setShowToast({ type: 'error', text: 'Se agregó el producto, pero su precio no coincide con el filtro activo.' });
                        }
                        applyBarcodeAutoPriceSelectionMeta(targetMatch.variety, targetMatch.priceIndex);
                        const currentQty = normalizeQuantity(
                            quantities[targetMatch.variety.id]?.[targetMatch.priceIndex] || 0
                        );
                        if (mode === 'weight') {
                            const nextQty = normalizeQuantity(currentQty + decoded.weightKg, 3);
                            handleQuantityChange(targetMatch.variety.id, targetMatch.priceIndex, nextQty);
                        } else if (mode === 'amount') {
                            const nextQty = normalizeQuantity(currentQty + decoded.amount, 3);
                            handleQuantityChange(targetMatch.variety.id, targetMatch.priceIndex, nextQty);
                        } else if (mode === 'price') {
                            const unitPrice = targetMatch.varietyPrice?.price || 0;
                            if (unitPrice > 0) {
                                const qtyDelta = normalizeQuantity(decoded.price / unitPrice, 3);
                                const nextQty = normalizeQuantity(currentQty + qtyDelta, 3);
                                handleQuantityChange(targetMatch.variety.id, targetMatch.priceIndex, nextQty);
                            }
                        }
                        match = targetMatch;
                    }
                }
            }
        }

        if (!match && trimmed.length <= 7) {
            const keys = buildShortBarcodeLookupKeys(trimmed);
            match = findVarietyPriceByBarcode(keys);
            if (!match) {
                anyMatch = findAnyVarietyPriceByBarcode(keys);
            }
            const targetMatch = match || anyMatch;
            if (targetMatch) {
                if (!match && anyMatch && clientType) {
                    setShowToast({ type: 'error', text: 'Se agregó el producto, pero su precio no coincide con el filtro activo.' });
                }
                applyBarcodeAutoPriceSelectionMeta(targetMatch.variety, targetMatch.priceIndex);
                handleQuantityIncrement(targetMatch.variety.id, targetMatch.priceIndex, 1);
                match = targetMatch;
            }
        }

        if (match) {
            applySearch('');
            barcodeInputRef.current?.focus();
            barcodeInputRef.current?.select();
        }
    };

    useEffect(() => {
        filterProducts(searchTerm, selectedCategory);
    }, [searchTerm, selectedCategory, products, clientType, resolveProductCategoryId, searchMode]);

    useEffect(() => {
        if (!clientType) return;
        const normalized = resolveSaleType(clientType);
        if (normalized !== clientType) {
            setClientType(normalized);
            setTypeSelection(normalized);
        }
    }, [clientType, resolveSaleType]);

    const handleClientTypeChange = async (newType) => {
        if (!canEditQuotation || isReadOnly) {
            setShowToast({ type: 'error', text: 'No tienes permisos para editar la cotización.' });
            return;
        }
        if (!newType) {
            setClientType('');
            setTypeSelection('');
            filterProducts(searchTerm, selectedCategory, '');
            return;
        }
        const resolvedType = resolveSaleType(newType);
        if (!resolvedType) return;
        setClientType(resolvedType);
        setTypeSelection(resolvedType);
        filterProducts(searchTerm, selectedCategory, resolvedType);
    };

    const setDataForQuotationDetails = () => {
        const orderedItems = buildItemsPayload();

        setQuotationDetails({
            Client: { name, rut, email, phone, address, city },
            items: orderedItems,
            itemsSubtotal,
            orderDiscountAmount,
            orderDiscountPercentage,
            totalAmount,
            id: quotationId,
            createdAt: new Date().toISOString(),
        });
    }

    useEffect(() => {
        setDataForQuotationDetails();
    }, [quantities, discounts, orderDiscountAmount, orderDiscountPercentage, totalAmount, itemsSubtotal, linePriceMeta, addedOrder, promotions]);

    useEffect(() => {
        if (!typeParam) return;
        const resolvedType = resolveSaleType(typeParam);
        if (resolvedType && resolvedType !== clientType) {
            setClientType(resolvedType);
            setTypeSelection(resolvedType);
        }
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('type');
        nextParams.delete('clientType');
        setSearchParams(nextParams, { replace: true });
    }, [typeParam, resolveSaleType, clientType, searchParams, setSearchParams]);

    useEffect(() => {
        if (!quotationRouteId && !clientIdParam && !quotationId && !clientId) {
            setShowQuotationCard(true);
        }
    }, [quotationRouteId, clientIdParam, quotationId, clientId]);

    const openQuotationModal = () => {
        setDataForQuotationDetails();
        setShowQuotationModal(true);
        setModalProduct(null);
        setModalVariety(null);
        setModalVarietyPrice(null);
    };
    usePosNavbarCart({ enabled: true, totalAmount, onOpen: openQuotationModal });

    const hasQuotationItems = useMemo(
        () =>
            Object.values(quantities || {}).some(
                (list) =>
                    Array.isArray(list)
                    && list.some((qty) => normalizeQuantity(qty) > 0)
            ),
        [quantities]
    );

    const resetQuotationState = () => {
        const emptyQuantities = buildEmptyProductMatrix(products);
        setModalVariety(null);
        setModalVarietyPrice(null);
        setModalProduct(null);
        setShowQuotationModal(false);
        setQuantities(emptyQuantities);
        setDiscounts(buildEmptyProductMatrix(products));
        setLinePriceMeta({});
        setAddedOrder([]);
        setItemsSubtotal(0);
        setTotalPrice(0);
        setOrderDiscountAmount(null);
        setOrderDiscountPercentage(null);
        setExcludedTaxIds([]);
        setQuotationId(null);
        setStatus('draft');
        setIsReadOnly(false);
        setName('');
        setRut('');
        setEmail('');
        setAddress('');
        setCity('');
        setPhone('');
        setGiro('');
        setClientId(null);
        setClientType('detalle');
        setTypeSelection('detalle');
        setClientPricingDiscountMap({});
        setQuoteClientPricingCategoryIds(null);
        setApplyQuoteClientPricingCategoryDiscounts(true);
        setQuoteClientPricingCategoryDirty(false);
        setClientMessage('');
        setClientMessageType('info');
        setSearchTerm('');
        setSearchVarietyResults([]);
        setSuggestedSearchVarietyResults([]);
        setSelectedCategory('all');
        setFilteredProducts(products);
        setQuotationDetails({
            Client: { name: '', rut: '', email: '', phone: '', address: '', city: '' },
            items: [],
            itemsSubtotal: 0,
            orderDiscountAmount: null,
            orderDiscountPercentage: null,
            totalAmount: 0,
            id: null,
            createdAt: new Date().toISOString(),
        });
        setShowQuotationCard(true);
        setTimeout(() => {
            barcodeInputRef.current?.focus();
            barcodeInputRef.current?.select();
        }, 0);
    };

    const handleQuotationTabSelect = async (selectedId, options = {}) => {
        if (selectedId) {
            if (!canUpdateQuotation) return;
            navigate(`/branches/${branchId}/quotations/${selectedId}/edit`);
            return;
        }
        if (!canCreateQuotation) return;

        if (options.skipSave) {
            resetQuotationState();
            navigate(`/branches/${branchId}/quotations/new`);
            return;
        }

        const shouldSave = hasQuotationItems || quotationId;
        let canReset = !shouldSave;

        if (shouldSave) {
            setIsLoading(true);
            try {
                const savedId = await saveQuotation();
                canReset = !!savedId;
            } catch (error) {
                console.error('Error saving quotation before starting a new one:', error);
                canReset = false;
            } finally {
                setIsLoading(false);
            }
        }

        if (canReset) {
            resetQuotationState();
            navigate(`/branches/${branchId}/quotations/new`);
        }
    };

    const handleRemoveItem = async (itemToRemove) => {
        if (!canEditQuotation) {
            setShowToast({ type: 'error', text: 'No tienes permisos para editar la cotización.' });
            return;
        }
        if (isReadOnly) {
            setShowToast({ type: 'info', text: 'Cotización en solo lectura: duplica para editar.' });
            return;
        }
        if (!Number.isInteger(itemToRemove.priceIndex)) {
            setShowToast({ type: 'error', text: 'Item sin priceIndex. No se puede eliminar de forma segura.' });
            console.warn('Cannot remove item without priceIndex.', itemToRemove);
            return;
        }
        const priceIndex = itemToRemove.priceIndex;
        if (priceIndex < 0) {
            setShowToast({ type: 'error', text: 'priceIndex inválido. No se puede eliminar de forma segura.' });
            console.warn('Cannot remove item with invalid priceIndex.', itemToRemove);
            return;
        }
        setIsLoading(true);

        const variety = products.flatMap(product => product.Varieties).find(v => v.id === itemToRemove.varietyId);

        const updatedQuantities = { ...quantities };
        const updatedDiscounts = { ...discounts };

        if (variety && priceIndex !== -1) {
            updatedQuantities[variety.id] = [...(updatedQuantities[variety.id] || [])];
            updatedQuantities[variety.id][priceIndex] = 0;

            updatedDiscounts[variety.id] = [...(updatedDiscounts[variety.id] || [])];
            updatedDiscounts[variety.id][priceIndex] = 0;
        }

        const updatedOrder = addedOrder.filter(
            i => !(i.varietyId === itemToRemove.varietyId && i.priceIndex === priceIndex)
        );

        const updatedItems = (quotationDetails?.items || []).filter(item =>
            !(item.varietyId === itemToRemove.varietyId && item.priceIndex === priceIndex)
        );

        const updatedTotals = computeTotals(
            products,
            updatedQuantities,
            updatedDiscounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        const updatedItemsSubtotal = updatedTotals.subtotal;
        const updatedTotal = updatedTotals.total;
        setItemsSubtotal(updatedTotals.subtotal);
        setTotalPrice(updatedTotals.total);

        setQuantities(updatedQuantities);
        setDiscounts(updatedDiscounts);
        setAddedOrder(updatedOrder);
        setLinePriceMeta((prev) => {
            const next = { ...prev };
            delete next[lineKey(itemToRemove.varietyId, priceIndex)];
            return next;
        });
        setQuotationDetails((prev) => ({
            ...prev,
            items: updatedItems,
            itemsSubtotal: updatedItemsSubtotal,
            totalAmount: updatedTotal
        }));

        if (quotationId) {
            try {
                await updateQuotation(branchId, quotationId, {
                    items: updatedItems,
                    totalAmount: updatedTotal,
                    clientId,
                    order: updatedOrder,
                    orderDiscountAmount,
                    orderDiscountPercentage,
                    ...quotationClientPricingPayload,
                    ...(canExcludeTaxes ? { excludedTaxIds } : {}),
                });
            } catch (error) {
                console.error("Error updating quotation:", error);
                const message = error?.response?.data?.message || error.message || 'Error actualizando la cotización.';
                setShowToast({ type: 'error', text: message });
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }
    };

    const handleQuantityChange = (varietyId, priceIndex, value) => {
        if (!canEditQuotation) {
            setShowToast({ type: 'error', text: 'No tienes permisos para editar la cotización.' });
            return;
        }
        if (isReadOnly) {
            setShowToast({ type: 'info', text: 'Cotización en solo lectura: duplica para editar.' });
            return;
        }
        const quantity = normalizeQuantity(value);
        if (quantity > MAX_PURCHASE_ITEM_QUANTITY) {
            setShowToast({
                type: 'error',
                text: `La cantidad excede el maximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`,
            });
            return;
        }

        const variety = products.flatMap((p) => p.Varieties).find((v) => v.id === varietyId);
        const price = variety?.VarietyPrices?.[priceIndex];
        if (variety && price && !isPriceAllowedByRegister(price)) {
            setShowToast({ type: 'error', text: 'Precio no válido para el tipo de venta.' });
            return;
        }
        setQuantities((prevQuantities) => {
            const newQuantities = { ...prevQuantities };
            newQuantities[varietyId] = [...newQuantities[varietyId]];
            newQuantities[varietyId][priceIndex] = quantity;

            if (quantity > 0 && !addedOrder.some(i => i.varietyId === varietyId && i.priceIndex === priceIndex)) {
                setAddedOrder((prev) => [{ varietyId, priceIndex }, ...prev]);
            }

            if (quantity === 0) {
                setAddedOrder((prev) =>
                    prev.filter(i => !(i.varietyId === varietyId && i.priceIndex === priceIndex))
                );
            }

            const total = calculateTotal(newQuantities);
            handleQuotationSave(newQuantities, total, discounts);
            return newQuantities;
        });
    };

    const handleQuantityIncrement = (varietyId, priceIndex, increment) => {
        if (!canEditQuotation) {
            setShowToast({ type: 'error', text: 'No tienes permisos para editar la cotización.' });
            return;
        }
        if (isReadOnly) {
            setShowToast({ type: 'info', text: 'Cotización en solo lectura: duplica para editar.' });
            return;
        }
        const variety = products.flatMap((p) => p.Varieties).find((v) => v.id === varietyId);
        const price = variety?.VarietyPrices?.[priceIndex];
        if (variety && price && !isPriceAllowedByRegister(price)) {
            setShowToast({ type: 'error', text: 'Precio no válido para el tipo de venta.' });
            return;
        }
        setQuantities((prevQuantities) => {
            const newQuantities = { ...prevQuantities };
            newQuantities[varietyId] = [...newQuantities[varietyId]];
            const nextQty = normalizeQuantity(
                Math.max(0, normalizeQuantity(newQuantities[varietyId][priceIndex] || 0) + increment)
            );
            if (nextQty > MAX_PURCHASE_ITEM_QUANTITY) {
                setShowToast({
                    type: 'error',
                    text: `La cantidad excede el maximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`,
                });
                return prevQuantities;
            }
            newQuantities[varietyId][priceIndex] = nextQty;

            const quantity = newQuantities[varietyId][priceIndex];

            if (quantity > 0 && !addedOrder.some(i => i.varietyId === varietyId && i.priceIndex === priceIndex)) {
                setAddedOrder((prev) => [{ varietyId, priceIndex }, ...prev]);
            }

            if (quantity === 0) {
                setAddedOrder((prev) =>
                    prev.filter(i => !(i.varietyId === varietyId && i.priceIndex === priceIndex))
                );
            }

            const total = calculateTotal(newQuantities);
            handleQuotationSave(newQuantities, total, discounts);
            return newQuantities;
        });
    };

    const handleDiscountChange = async (varietyId, priceIndex, value) => {
        const offerPrice = products.flatMap((product) => product.Varieties || []).find((variety) => Number(variety.id) === Number(varietyId))?.VarietyPrices?.[priceIndex];
        if (isFormatOfferPrice(offerPrice)) {
            setShowToast({ type: 'info', text: 'La oferta tiene un precio final fijo y no admite descuentos adicionales.' });
            return;
        }
        if (!canEditQuotation) {
            setShowToast({ type: 'error', text: 'No tienes permisos para editar la cotización.' });
            return;
        }
        if (isReadOnly) {
            setShowToast({ type: 'info', text: 'Cotización en solo lectura: duplica para editar.' });
            return;
        }
        const parsedValue = parseInt(value);
        const discount = isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;
        const key = lineKey(varietyId, priceIndex);
        setLinePriceMeta((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                discountSource: discount > 0 ? 'manual' : 'none',
                manualDiscountCandidate: discount > 0 ? discount : null,
            },
        }));

        setDiscounts((prevDiscounts) => {
            const newDiscounts = { ...prevDiscounts };
            newDiscounts[varietyId] = [...newDiscounts[varietyId]];
            newDiscounts[varietyId][priceIndex] = discount;

            const total = calculateTotal(quantities, newDiscounts);
            handleQuotationSave(quantities, total, newDiscounts);
            return newDiscounts;
        });
    }

    const calculateTotal = (
        quantitiesData,
        discountsData,
        overrideOrderDiscountAmount,
        overrideOrderDiscountPercentage,
        productsOverride
    ) => {
        const totals = computeTotals(
            Array.isArray(productsOverride) ? productsOverride : products,
            quantitiesData,
            discountsData,
            overrideOrderDiscountAmount,
            overrideOrderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
        return totals.total;
    };

    const decideIfToShowModalClientRegistration = (varietyPrice) => {
        if (modalVariety && varietyPrice && !isPriceAllowedForType('', modalVariety, varietyPrice)) {
            setShowToast({ type: 'error', text: 'Este precio no está disponible para este tipo de venta.' });
            return;
        }
        setModalVarietyPrice(varietyPrice);
    }

    const buildItemsPayload = ({
        productsData = products,
        quantitiesData = quantities,
        discountsData = discounts,
        linePriceMetaData = linePriceMeta,
        orderDiscountAmountData = orderDiscountAmount,
        orderDiscountPercentageData = orderDiscountPercentage,
    } = {}) => {
        const rawItems = (productsData || []).reduce((acc, product) => {
            const productItems = product.Varieties.flatMap(variety =>
                variety.VarietyPrices?.map((price, index) => {
                    if (!isPriceAllowedByRegister(price)) return null;
                    const quantity = normalizeQuantity(quantitiesData?.[variety.id]?.[index] || 0);
                    if (quantity <= 0) return null;
                    const meta = linePriceMetaData[lineKey(variety.id, index)] || null;
                    const lineDiscount = discountsData?.[variety.id]?.[index] || 0;
                    const canSelectPriceType = varietySupportsBarcodePriceTypeSelection(variety);
                    const resolvedPriceType = normalizePriceTypeValue(meta?.priceType)
                        || resolvePriceClientType(price);
                    return buildPromotionLineItem({
                        product,
                        variety,
                        price,
                        priceIndex: index,
                        quantity,
                        discount: lineDiscount,
                        extra: {
                            discountSource: meta?.discountSource || (lineDiscount > 0 ? 'manual' : 'none'),
                            clientPricingDiscountType: meta?.clientPricingDiscountType,
                            clientPricingDiscountValue: meta?.clientPricingDiscountValue,
                            clientPricingCandidateRules: meta?.clientPricingCandidateRules,
                            clientPricingRuleType: meta?.clientPricingRuleType,
                            clientPricingMarginReductionPercent: meta?.clientPricingMarginReductionPercent,
                            clientPricingReferenceCost: meta?.clientPricingReferenceCost,
                            manualDiscount: meta?.manualDiscountCandidate
                                ?? (meta?.discountSource === 'manual' ? lineDiscount : 0),
                            clientPricingCategoryId: meta?.clientPricingCategoryId || null,
                            clientPricingCategoryName: meta?.clientPricingCategoryName || null,
                            clientPricingDiscountType: meta?.clientPricingDiscountType || null,
                            clientPricingDiscountValue: meta?.clientPricingDiscountValue ?? null,
                            priceType: resolvedPriceType,
                            priceTypeOverride: canSelectPriceType && Boolean(meta?.priceTypeOverride),
                            ...(canSelectPriceType && meta?.autoPriceSelection ? { autoPriceSelection: true } : {}),
                        },
                    });
                }) || []
            ).filter((item) => item && item.quantity > 0 && item.price !== null && item.price !== undefined);
            return acc.concat(productItems);
        }, []);
        return applyPromotionsToLineItems(
            orderAndIndexLineItems(rawItems),
            promotions,
            'quotation',
            {
                orderDiscountAmount: orderDiscountAmountData,
                orderDiscountPercentage: orderDiscountPercentageData,
                taxProfileByVarietyId: varietyTaxProfileById,
                now: promotionNow,
            }
        );
    };

    const computeItemTotals = () => {
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        return {
            itemsSubtotal: totals.subtotal,
            orderDiscountAmount,
            orderDiscountPercentage,
            totalAmount: totals.total,
        };
    };

    const saveQuotation = async (idClient) => {
        if (!canEditQuotation) {
            setShowToast({ type: 'error', text: 'No tienes permisos para guardar la cotización.' });
            return null;
        }
        if (isReadOnly) {
            setShowToast({ type: 'info', text: 'Cotización en solo lectura: duplica para editar.' });
            return null;
        }
        const offerIssue = getFormatOfferCartMessage(products, quantities);
        if (offerIssue) { setShowToast({ type: 'error', text: offerIssue }); return null; }
        const rawItems = buildItemsPayload();
        const invalidQuantityItem = findInvalidPurchaseItemQuantity(rawItems);
        if (invalidQuantityItem) {
            setShowToast({ type: 'error', text: buildPurchaseItemQuantityLimitMessage(invalidQuantityItem) });
            return null;
        }
        if (rawItems.length === 0) {
            setShowToast({ type: 'error', text: 'Agrega al menos un producto a la cotización.' });
            return null;
        }
        const items = compactQuotationItemsPayload(rawItems);
        const totals = computeItemTotals();
        let response;
        try {
            if (!quotationId) {
                response = await createQuotation(branchId, {
                    items,
                    clientId: idClient || clientId,
                    order: addedOrder,
                    ...totals,
                    ...quotationClientPricingPayload,
                    ...(canExcludeTaxes ? { excludedTaxIds } : {}),
                });
                setQuotationId(response?.quotation?.id || null);
                setStatus(response?.quotation?.status || 'draft');
            } else {
                response = await updateQuotation(branchId, quotationId, {
                    items,
                    clientId: idClient || clientId,
                    order: addedOrder,
                    ...totals,
                    ...quotationClientPricingPayload,
                    ...(canExcludeTaxes ? { excludedTaxIds } : {}),
                });
                setStatus(response?.quotation?.status || status);
            }
            return response?.quotation?.id || quotationId;
        } catch (error) {
            const message = error?.response?.data?.message || error.message || 'Error guardando la cotización.';
            setShowToast({ type: 'error', text: message });
            return null;
        }
    };

    const handleSend = async () => {
        if (!canSendQuotation) {
            setShowToast({ type: 'error', text: 'No tienes permisos para enviar la cotización.' });
            return;
        }
        if (!email) {
            setClientMessage('Necesitas agregar un email de cliente para enviar la cotización.');
            setClientMessageType('error');
            setShowToast({ type: 'error', text: 'Necesitas agregar un email de cliente para enviar la cotización.' });
            return;
        }
        if (quotationId && isImmutableQuotationStatus(status)) {
            setShowToast({ type: 'info', text: 'La cotización está en solo lectura.' });
            return;
        }
        setActionBusy(true);
        const idQuotation = await saveQuotation();
        if (!idQuotation) {
            setActionBusy(false);
            return;
        }
        try {
            await sendQuotation(branchId, idQuotation, {});
            setShowToast({ type: 'success', text: 'Cotización enviada.' });
            setStatus('sent');
            setIsReadOnly(true);
        } catch (error) {
            setShowToast({ type: 'error', text: error.message || 'No se pudo enviar la cotización' });
        } finally {
            setActionBusy(false);
            navigate(`/branches/${branchId}/quotations/${idQuotation}`);
        }
    };

    const handleConvert = async () => {
        if (!canConvertQuotation) {
            setShowToast({
                type: 'error',
                text: status === 'accepted'
                    ? 'La cotización ya fue aceptada.'
                    : 'Solo se pueden convertir cotizaciones enviadas.',
            });
            return;
        }
        setActionBusy(true);
        const idQuotation = quotationId;
        if (!idQuotation) {
            setActionBusy(false);
            return;
        }
        try {
            const response = await requestConversion(idQuotation, totalAmount);
            if (!response) {
                setActionBusy(false);
                return;
            }
            const purchaseId = response?.purchase?.id;
            if (purchaseId) {
                setShowToast({ type: 'success', text: 'Cotización convertida. Redirigiendo a registrar pago.' });
                navigate(`/branches/${branchId}/purchases/${purchaseId}/receipt`);
                setActionBusy(false);
                return;
            }
            setShowToast({ type: 'error', text: 'Cotización convertida, pero no se encontró la venta creada.' });
        } catch (error) {
            setShowToast({ type: 'error', text: error?.message || 'No se pudo convertir la cotización' });
            setActionBusy(false);
            return;
        }
        setActionBusy(false);
        navigate(`/branches/${branchId}/quotations/${idQuotation}`);
    };

    const handleFinishQuotation = async () => {
        if (!canModifyQuotation) {
            setShowToast({ type: 'error', text: 'No tienes permisos para guardar la cotización.' });
            return;
        }
        setActionBusy(true);
        const idQuotation = await saveQuotation();
        if (idQuotation) {
            setShowToast({ type: 'success', text: 'Borrador guardado.' });
            setShowQuotationModal(false);
            navigate(`/branches/${branchId}/quotations/${idQuotation}`, { replace: true });
        }
        setActionBusy(false);
    };

    const handleQuotationSave = (newQuantities, total, newDiscounts) => {
        calculateTotal(newQuantities, newDiscounts);
    };

    const findVarietyForItem = (item) =>
        products.flatMap((product) => product.Varieties).find((variety) => variety.id === item.varietyId);

    const getItemQuantity = (item) =>
        normalizeQuantity(quantities[item.varietyId]?.[item.priceIndex] || 0);

    const getQuotationLineIssue = (item) => getFormatOfferLineIssue(item, findVarietyForItem(item));

    const getItemPriceTypeOptions = (item, variety) => {
        if (!variety) return [];
        if (!varietySupportsBarcodePriceTypeSelection(variety)) return [];
        const seen = new Set();
        const options = [];
        (variety.VarietyPrices || []).forEach((price) => {
            if (!isPriceAllowedByRegister(price)) return;
            const type = normalizePriceTypeValue(resolvePriceClientType(price));
            if (!type || seen.has(type)) return;
            seen.add(type);
            options.push({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) });
        });
        return options.length > 1 ? options : [];
    };

    const handleItemPriceTypeChange = (item, nextTypeValue) => {
        if (!canOverrideLinePriceType || !canModifyQuotation) return;
        const variety = findVarietyForItem(item);
        if (!variety) return;
        if (!varietySupportsBarcodePriceTypeSelection(variety)) {
            setShowToast({
                type: 'error',
                text: 'Solo se puede cambiar el formato cuando la variedad comparte el mismo codigo de barras en todos sus precios.',
            });
            return;
        }
        const currentIndex = Number.isInteger(item?.priceIndex) ? item.priceIndex : 0;
        const currentKey = lineKey(variety.id, currentIndex);
        const normalizedType = normalizePriceTypeValue(nextTypeValue);

        if (!normalizedType) {
            setLinePriceMeta((prev) => ({
                ...prev,
                [currentKey]: {
                    priceType: normalizePriceTypeValue(item?.priceType)
                        || resolvePriceClientType(variety.VarietyPrices?.[currentIndex]),
                    priceTypeOverride: false,
                    autoPriceSelection: Boolean(prev[currentKey]?.autoPriceSelection),
                },
            }));
            return;
        }

        const targetCandidates = (variety.VarietyPrices || [])
            .map((price, index) => ({ price, index }))
            .filter(({ price }) =>
                !isFormatOfferPrice(price) && isFormatOfferSelectable(price)
                && isPriceAllowedByRegister(price)
                && normalizePriceTypeValue(resolvePriceClientType(price)) === normalizedType
            )
            .sort((a, b) => Number(a.price?.price || 0) - Number(b.price?.price || 0));
        const targetIndex = targetCandidates[0]?.index;
        if (!Number.isInteger(targetIndex) || targetIndex < 0) return;

        if (targetIndex === currentIndex) {
            setLinePriceMeta((prev) => ({
                ...prev,
                [currentKey]: {
                    priceType: normalizedType,
                    priceTypeOverride: true,
                    autoPriceSelection: false,
                },
            }));
            return;
        }

        const currentQty = normalizeQuantity(quantities?.[variety.id]?.[currentIndex] || item?.quantity || 0);
        if (currentQty <= 0) return;

        const nextQuantities = { ...quantities };
        const nextDiscounts = { ...discounts };
        const nextOrder = [...addedOrder];
        const targetKey = lineKey(variety.id, targetIndex);

        nextQuantities[variety.id] = [...(nextQuantities[variety.id] || [])];
        nextDiscounts[variety.id] = [...(nextDiscounts[variety.id] || [])];

        const sourceDiscount = Number(nextDiscounts[variety.id][currentIndex] || 0);
        nextQuantities[variety.id][targetIndex] = normalizeQuantity(
            (nextQuantities[variety.id][targetIndex] || 0) + currentQty
        );
        nextQuantities[variety.id][currentIndex] = 0;
        if (nextDiscounts[variety.id][targetIndex] == null) {
            nextDiscounts[variety.id][targetIndex] = sourceDiscount;
        }
        nextDiscounts[variety.id][currentIndex] = 0;

        const currentOrderIndex = nextOrder.findIndex(
            (entry) => entry.varietyId === variety.id && entry.priceIndex === currentIndex
        );
        if (currentOrderIndex !== -1) {
            nextOrder[currentOrderIndex] = { varietyId: variety.id, priceIndex: targetIndex };
        } else if (!nextOrder.some((entry) => entry.varietyId === variety.id && entry.priceIndex === targetIndex)) {
            nextOrder.unshift({ varietyId: variety.id, priceIndex: targetIndex });
        }

        setQuantities(nextQuantities);
        setDiscounts(nextDiscounts);
        setAddedOrder(nextOrder);
        setLinePriceMeta((prev) => {
            const next = { ...prev };
            delete next[currentKey];
            next[targetKey] = {
                priceType: normalizedType,
                priceTypeOverride: true,
                autoPriceSelection: false,
            };
            return next;
        });
        calculateTotal(nextQuantities, nextDiscounts);
    };

    const closeAllProductModals = () => {
        setModalVarietyPrice(null);
        setModalVariety(null);
        setModalProduct(null);
        setTimeout(() => {
            searchInputRef?.current?.focus();
        }, 100);
    };

    const canUsePriceCardAction = (price) => {
        if (!modalVariety || !price || !isPriceAllowedByRegister(price)) return false;
        if (!isPriceAllowedForType('', modalVariety, price)) {
            setShowToast({ type: 'error', text: 'Este precio no está disponible para este tipo de venta.' });
            return false;
        }
        return true;
    };

    const handlePriceCardIncrement = (price, priceIndex) => {
        if (!canUsePriceCardAction(price)) return;
        applyBarcodeAutoPriceSelectionMeta(modalVariety, priceIndex);
        handleQuantityIncrement(modalVariety.id, priceIndex, 1);
    };

    const handlePriceCardDecrement = (price, priceIndex) => {
        if (!canUsePriceCardAction(price)) return;
        applyBarcodeAutoPriceSelectionMeta(modalVariety, priceIndex);
        handleQuantityIncrement(modalVariety.id, priceIndex, -1);
    };

    const handlePriceCardKeypad = (price, priceIndex) => {
        if (!canUsePriceCardAction(price)) return;
        applyBarcodeAutoPriceSelectionMeta(modalVariety, priceIndex);
        decideIfToShowModalClientRegistration(price);
    };

    const cartWrapperClassName = 'hidden md:flex md:flex-col md:justify-end fixed top-[220px] bottom-0 app-fixed-left-pane px-4 z-0';

    const cartPanelClassName = 'bg-white shadow-lg rounded-lg p-4 md:overflow-y-auto md:max-h-[calc(86vh-260px)] md:h-[calc(86vh-260px)]';

    const mainContentClassName = 'flex-1 md:pl-[max(440px,33.333333%)] p-4 overflow-auto';

    const isVarietySoldOut = isResolvedVarietySoldOut;
    const isProductSoldOut = isResolvedProductSoldOut;

    const renderVarietyResultCard = ({ product, variety }) => {
        const imageSrc = variety.varietyImage || product.productImage || 'https://placehold.co/200';
        const isSoldOut = isVarietySoldOut(variety);
        const isSelectable = !isSoldOut;

        return (
            <PosProductCard
                key={`${product.id}-${variety.id}`}
                productName={product.productName}
                subtitle={variety.varietyName}
                imageSrc={imageSrc}
                disabled={!isSelectable}
                badge={isSoldOut ? 'Agotado' : ''}
                onClick={() => {
                    setModalProduct(product);
                    setModalVariety(variety);
                }}
            />
        );
    };

    const renderProductCard = (product) => {
        const imageSrc = product.productImage || 'https://placehold.co/200';
        const isSoldOut = isProductSoldOut(product);

        return (
            <PosProductCard
                key={product.id}
                productName={product.productName}
                imageSrc={imageSrc}
                badge={isSoldOut ? 'Agotado' : ''}
                onClick={() => setModalProduct(product)}
            />
        );
    };

    const renderCategoryPicker = (itemsWrapperClassName = '') => (
        <CategoryFilterBar
            categories={displayCategories}
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
            includeAll
            allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
            loading={categoriesLoading}
            loadingMessage={<InlineLoading label="Cargando categorías" />}
            error={categoriesError}
            emptyMessage="No hay categorías disponibles."
            isEmpty={displayCategories.length === 0}
            itemsWrapperClassName={itemsWrapperClassName}
        />
    );

    const handleQuoteClientPricingApplyChange = (event) => {
        if (!canManageClientPricing || isReadOnly) return;
        setApplyQuoteClientPricingCategoryDiscounts(event.target.checked);
        setQuoteClientPricingCategoryDirty(true);
    };

    const handleQuoteClientPricingCategoryChange = (ids) => {
        if (!canManageClientPricing || isReadOnly) return;
        setQuoteClientPricingCategoryIds(ids);
        setApplyQuoteClientPricingCategoryDiscounts(true);
        setQuoteClientPricingCategoryDirty(true);
    };

    const renderClientPricingCategoryControls = () => {
        if (!canManageClientPricing) return null;
        return (
            <div className="rounded-lg border border-blue-100 bg-white px-3 py-2 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <label className="flex min-w-0 flex-1 items-center gap-2 text-xs font-semibold text-gray-700">
                        <input
                            type="checkbox"
                            checked={applyQuoteClientPricingCategoryDiscounts}
                            onChange={handleQuoteClientPricingApplyChange}
                            disabled={isReadOnly}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <FiTag className="shrink-0 text-blue-600" />
                        <span className="truncate">Aplicar descuentos cliente</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input type="checkbox" checked={quoteClientPricingCategoryIds === null}
                            onChange={(event) => handleQuoteClientPricingCategoryChange(event.target.checked ? null : [])}
                            disabled={isReadOnly || !applyQuoteClientPricingCategoryDiscounts} />
                        Usar categorías del cliente
                    </label>
                </div>
                {applyQuoteClientPricingCategoryDiscounts && quoteClientPricingCategoryIds !== null && (
                    <div className="mt-3"><ClientPricingCategoryMultiSelect
                        value={quoteClientPricingCategoryIds}
                        onChange={handleQuoteClientPricingCategoryChange}
                        categories={clientPricingCategories}
                        loading={clientPricingCategoriesLoading}
                        disabled={isReadOnly}
                    /></div>
                )}
            </div>
        );
    };

    return (
        <>
            {conversionDialog}
            {isLoading && <LoadingOverlay />}
            {showToast && (
                <div
                    className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded shadow text-white ${
                        showToast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
                    }`}
                >
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-sm">{showToast.text}</span>
                        <button
                            onClick={() => setShowToast(null)}
                            className="text-white text-xs underline"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
            {/* Full-Height Page Wrapper */}
            <div className="flex flex-col min-h-[90vh] bg-gray-100">
                {canReadQuotation && (
                    <PendingQuotationTabs
                        branchId={branchId}
                        activeQuotationId={quotationId}
                        activeQuotationLiveId={quotationId}
                        activeQuotationLiveTotal={totalAmount}
                        onSelect={handleQuotationTabSelect}
                        tone="navbar"
                        canDelete={canDeleteQuotation}
                        canCreate={canCreateQuotation}
                        canSelect={canUpdateQuotation}
                    />
                )}

                <DocumentHeader
                    stickyOffsetClassName={canReadQuotation ? 'top-14' : 'top-0'}
                    title={`Cotización N° ${quotationId || 'Nueva'}`}
                    onTitleClick={openQuotationModal}
                    showCartButton={false}
                    headerActions={
                        <ProductViewToggle
                            value={productView}
                            onChange={setProductView}
                            allowedViews={['grid']}
                        />
                    }
                    totalAmount={totalAmount}
                    totalAmountClassName="hidden md:inline text-blue-500"
                    clientName={name}
                    clientRut={rut}
                    clientGiro={giro}
                    clientType={clientType}
                    emptyClientLabel="Detalle sin cliente"
                    variant="quotation"
                    clientMessage={clientMessage}
                    clientMessageType={clientMessageType}
                    canChangeClientType={canChangeClientType && !isReadOnly}
                    clientTypeOptions={productFormatOptions}
                    allowClientTypeToggleOff
                    clientTypeTitle="Filtro de formato:"
                    emptyClientTypeValueLabel="Todos"
                    onSelectClientType={handleClientTypeChange}
                    onChangeClientType={() => {
                        setTypeSelection(clientType || '');
                        setShowTypeModal(true);
                    }}
                    clientActions={
                        <button
                            type="button"
                            onClick={() => setShowQuotationCard(true)}
                            disabled={isReadOnly || (!canReadClient && !canCreateClient)}
                            className={`text-xs px-2 py-1 rounded border ${
                                isReadOnly || (!canReadClient && !canCreateClient)
                                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                    : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                            }`}
                        >
                            {name ? 'Cambiar cliente' : 'Agregar cliente'}
                        </button>
                    }
                    middleContent={
                        <div className="w-full max-w-[560px] space-y-2">
                            {renderClientPricingCategoryControls()}
                            {renderCategoryPicker()}
                        </div>
                    }
                    rightContent={
                        <div className={`${mobileSearchVisible ? 'block' : 'hidden'} md:block`}>
                            <ProductSearchInput
                                ref={searchInputRef}
                                type="text"
                                placeholder="Buscar producto..."
                                value={searchTerm}
                                products={searchableProducts}
                                onChange={handleSearch}
                                onKeyDown={handleQuickEntryKeyDown}
                                onFocus={(event) => event.target.select()}
                                className="w-full px-3 py-2.5 text-base md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    }
                />

                {/* Scrollable Content */}
                <div className="relative flex">
                    {/* 🧾 Cart Sidebar (list view expands to small screens) */}
                    <div className={cartWrapperClassName}>
                        <CartPanel
                            items={quotationDetails?.items || []}
                            getVariety={findVarietyForItem}
                            getItemQuantity={getItemQuantity}
                            onItemClick={(item, variety) => {
                                if (!variety) return;
                                setModalVariety(variety);
                                setModalVarietyPrice(null);
                            }}
                            onRemoveItem={handleRemoveItem}
                            canEdit={canModifyQuotation}
                            canOverridePriceType={canModifyQuotation && canOverrideLinePriceType}
                            getItemPriceTypeOptions={getItemPriceTypeOptions}
                            onItemPriceTypeChange={handleItemPriceTypeChange}
                            getItemPricing={resolveCartItemPricing}
                            getItemValidationError={getQuotationLineIssue}
                            showClientPricingCategoryLabels={canManageClientPricing}
                            headerContent={
                                <ProductSearchInput
                                    ref={barcodeInputRef}
                                    type="text"
                                    placeholder="Nombre o código de barras..."
                                    value={searchTerm}
                                    products={searchableProducts}
                                    onChange={handleSearch}
                                    onKeyDown={handleQuickEntryKeyDown}
                                    onFocus={(event) => event.target.select()}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            }
                            panelClassName={cartPanelClassName}
                            footerClassName="flex flex-col gap-1 mt-4 bottom-0 pb-1"
                            itemsSubtotal={itemsSubtotal}
                            orderDiscountPercentage={orderDiscountPercentage}
                            orderDiscountAmount={orderDiscountAmount}
                            totalAmount={totalAmount}
                            footerActions={
                                <div className="flex items-center gap-2 mt-2 flex-nowrap overflow-x-auto max-w-full">
                                    <div className="flex items-center gap-2 flex-nowrap shrink-0">
                                        {canModifyQuotation && (
                                            <button
                                                onClick={() => setShowOrderDiscountModal(true)}
                                                className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-3 rounded text-sm flex items-center gap-2"
                                            >
                                                <FiPercent />
                                                <span className="hidden md:inline">Descuento</span>
                                            </button>
                                        )}
                                        {canModifyQuotation && canExcludeTaxes && (
                                            <button
                                                onClick={() => setShowTaxModal(true)}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold p-3 rounded text-sm flex items-center justify-center disabled:opacity-60"
                                                disabled={taxesLoading || !!taxesError || taxes.length === 0}
                                            >
                                                <FiPercent />
                                            </button>
                                        )}
                                    </div>
                                    <div className="ml-auto flex items-center gap-2 flex-nowrap shrink-0">
                                        {canEditQuotation && (
                                            <button
                                                onClick={handleFinishQuotation}
                                                disabled={actionBusy || isReadOnly}
                                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded text-sm disabled:opacity-60"
                                            >
                                                Guardar cotización
                                            </button>
                                        )}
                                    </div>
                                </div>
                            }
                        />
                    </div>
                </div>

                {/* 👉 Main Product Content (shifted right) */}
                <div className={mainContentClassName}>
                    <div className="max-w-7xl mx-auto">
                        {!hasActiveSearch && (
                            <div className="mb-4 space-y-2 lg:hidden">
                                {renderClientPricingCategoryControls()}
                                {renderCategoryPicker()}
                            </div>
                        )}

                        {/* Search Results */}
                        {searchTerm && searchVarietyResults.length > 0 && (
                            <div className="space-y-4 mb-4">
                                <ProductGrid view={productView}>
                                    {searchVarietyResults.map(renderVarietyResultCard)}
                                </ProductGrid>
                            </div>
                        )}

                        {searchTerm && suggestedSearchVarietyResults.length > 0 && (
                            <div className="space-y-4 mb-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Sugeridos
                                </div>
                                <ProductGrid view={productView}>
                                    {suggestedSearchVarietyResults.map(renderVarietyResultCard)}
                                </ProductGrid>
                            </div>
                        )}

                        {/* Product Grid */}
                        <div className="space-y-8">
                            {categoriesToRender.map((category) => {
                                const productsInCategory = filteredProducts
                                    .filter((p) => resolveProductCategoryId(p) === category.id)
                                    .sort((a, b) => a.productName.localeCompare(b.productName)); // sort alphabetically

                                if (productsInCategory.length === 0) return null;

                                return (
                                    <div key={category.id}>
                                        {/* Category Title */}
                                        <div className="flex items-center gap-4 my-2">
                                            <div className="flex-grow border-t border-gray-300"></div>
                                            <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                                                {category.name || 'Categoria'}
                                            </h2>
                                            <div className="flex-grow border-t border-gray-300"></div>
                                        </div>

                                        {/* Products Grid */}
                                        <ProductGrid view={productView}>
                                            {productsInCategory.map(renderProductCard)}
                                        </ProductGrid>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            {!showQuotationModal && (
                <button
                    type="button"
                    onClick={toggleMobileSearch}
                    disabled={mobileSearchVisible && hasActiveSearch}
                    className={`md:hidden fixed bottom-6 right-4 z-40 rounded-full shadow-lg p-3 ${
                        mobileSearchVisible && hasActiveSearch
                            ? 'bg-blue-300 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    aria-label={mobileSearchVisible ? 'Ocultar búsqueda' : 'Mostrar búsqueda'}
                >
                    {mobileSearchVisible && !hasActiveSearch ? <FiX size={20} /> : <FiSearch size={20} />}
                </button>
            )}

            {/* Modal Component */}
            {modalVariety && (
                <VarietyDetailModal
                    variety={modalVariety}
                    promotionNow={promotionNow}
                    onClose={closeModalVariety}
                    priceFilter={(price) => isPriceAllowedForType('', modalVariety, price)}
                    formatOptions={productFormatOptions}
                    resolveDisplayPrice={resolveVarietyPriceDisplay}
                    quantities={quantities}
                    discounts={discounts}
                    linePriceMeta={linePriceMeta}
                    onPriceIncrement={canModifyQuotation ? handlePriceCardIncrement : null}
                    onPriceDecrement={canModifyQuotation ? handlePriceCardDecrement : null}
                    onPriceKeypad={canModifyQuotation ? handlePriceCardKeypad : null}
                    onBackToVarieties={modalProduct ? () => {
                        setModalVariety(null);
                        setModalVarietyPrice(null);
                    } : null}
                    onConfirmClose={closeAllProductModals}
                    showScaleSummary={false}
                />
            )}
            <DiscountModal
                isOpen={showDiscountModal}
                onClose={() => setShowDiscountModal(false)}
                variety={modalVariety}
                varietyPrice={modalVarietyPrice}
                handleDiscountChange={handleDiscountChange}
                discounts={discounts}
                closeEverything={
                    () => {
                        setModalVarietyPrice(null);
                        setModalVariety(null);
                        setModalProduct(null);
                        setShowDiscountModal(false);
                        setTimeout(() => {
                            searchInputRef?.current?.focus();
                        }, 100);
                    }
                }
            />
            <OrderDiscountModal
                isOpen={showOrderDiscountModal}
                onClose={() => setShowOrderDiscountModal(false)}
                itemsSubtotal={itemsSubtotal}
                discountableSubtotal={Math.max(itemsSubtotal - computeCartNonDiscountableSubtotal({
                    items: quotationDetails?.items || [],
                    getVariety: findVarietyForItem,
                    getItemQuantity,
                    getItemPricing: resolveCartItemPricing,
                }), 0)}
                orderDiscountAmount={orderDiscountAmount}
                orderDiscountPercentage={orderDiscountPercentage}
                onApply={(amount, percentage) => {
                    if (!canModifyQuotation) return;
                    setOrderDiscountAmount(percentage != null ? null : amount);
                    setOrderDiscountPercentage(percentage != null ? percentage : null);
                    calculateTotal(quantities, discounts, amount, percentage);
                }}
            />
            <TaxExclusionModal
                isOpen={showTaxModal}
                onClose={() => setShowTaxModal(false)}
                taxes={taxes}
                excludedTaxIds={excludedTaxIds}
                onChange={(ids) => setExcludedTaxIds(ids)}
                disabled={!canModifyQuotation || !canExcludeTaxes || taxesLoading || !!taxesError}
            />
            {modalVariety && (
                <Modal
                    isOpen={!!modalVarietyPrice}
                    onClose={closeModal}
                    closeModalVariety={closeModalVariety}
                    closeModalProduct={() => {
                        setModalProduct(null); setTimeout(() => {
                            searchInputRef?.current?.focus();
                        }, 100);
                    }}
                    variety={modalVariety}
                    edit={!isReadOnly && canEditQuotation}
                    quantities={quantities}
                    varietyPrice={modalVarietyPrice}
                    handleQuantityChange={handleQuantityChange}
                    handleQuantityIncrement={handleQuantityIncrement}
                    setShowDiscountModal={setShowDiscountModal}
                    discounts={discounts}
                    canDiscount={canModifyQuotation}
                    resolveDisplayPrice={resolveVarietyPriceDisplay}
                />
            )}
            <ModalQuotationClientPicker
                isOpen={showQuotationCard}
                onClose={setShowQuotationCard}
                setClientId={handleSetClientId}
                setName={setName}
                setRut={setRut}
                setEmail={setEmail}
                setAddress={setAddress}
                setCity={setCity}
                setPhone={setPhone}
                clientType={clientType}
                setClientType={(t) => { setClientType(t); setTypeSelection(t); }}
                clientTypeOptions={productFormatOptions}
                canReadClient={canReadClient}
                canCreateClient={canCreateClient}
            />
            {showQuotationModal && (
                <QuotationSummaryModal
                    isOpen={showQuotationModal}
                    onClose={() => setShowQuotationModal(false)}
                    client={{ name, rut, email, phone, address, city }}
                    clientType={clientType}
                    items={quotationDetails?.items || []}
                    itemsSubtotal={itemsSubtotal}
                    orderDiscountAmount={orderDiscountAmount}
                    orderDiscountPercentage={orderDiscountPercentage}
                    totalAmount={totalAmount}
                    onSaveDraft={handleFinishQuotation}
                    onSend={handleSend}
                    onConvert={handleConvert}
                    disableActions={actionBusy}
                    canSaveDraft={canEditQuotation && !isReadOnly}
                    canSend={canSendQuotation && !isReadOnly}
                    canConvert={canConvertQuotation}
                    getItemValidationError={getQuotationLineIssue}
                />
            )}
            {showTypeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(event) => event.stopPropagation()}>
                    <div
                        className="bg-white rounded-lg shadow max-w-md w-full p-4 mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-bold">Seleccionar tipo de venta</h3>
                            <button onClick={() => setShowTypeModal(false)} className="text-gray-600 hover:text-black">✕</button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap gap-1 border-b border-gray-200">
                                {[{ value: '', label: 'Todos' }, ...productFormatOptions].map((option) => {
                                    const isActive = typeSelection === option.value;
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            aria-pressed={isActive}
                                            onClick={() => setTypeSelection(option.value)}
                                            disabled={isReadOnly}
                                            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px rounded-t-md transition ${
                                                isActive
                                                    ? 'bg-blue-50 text-blue-700 border-blue-600'
                                                    : 'bg-transparent text-gray-600 border-transparent hover:text-gray-800 hover:bg-gray-50'
                                            }`}
                                        >
                                            {option.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex justify-end gap-2 mt-2">
                                <button
                                    onClick={() => setShowTypeModal(false)}
                                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => {
                                        handleClientTypeChange(typeSelection);
                                        setShowTypeModal(false);
                                    }}
                                    disabled={isReadOnly}
                                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm disabled:opacity-60"
                                >
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Product Modal */}
            {modalProduct && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30" onClick={(event) => event.stopPropagation()}>
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full mx-4 relative flex flex-col"
                        style={{ maxHeight: "80vh", display: "flex" }} // Ensures it doesn't exceed 80% of screen height
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Scrollable Content */}
                        <div className="overflow-y-auto px-2" style={{ maxHeight: "70vh" }}>
                            {/* Close Button */}
                            <button onClick={() => {
                                setModalProduct(null); setTimeout(() => {
                                    searchInputRef?.current?.focus();
                                }, 100);
                            }} className="absolute top-2 right-2 text-gray-600 hover:text-black bg-white p-1 rounded-full">
                                ✕
                            </button>

                            {/* Centered Image with Increased Size */}
                            <div className="flex justify-center items-center mb-4">
                                <img
                                    src={modalProduct.productImage || "https://placehold.co/300"}
                                    alt={modalProduct.productName}
                                    className="w-32 h-32 object-cover rounded-lg"
                                />
                            </div>

                            <h2 className="text-xl font-bold text-center">Variedades de {modalProduct.productName}</h2>

                            {(() => {
                                const visibleVarieties = filterVarietiesByGroup(
                                    modalProduct.Varieties.filter((variety) => varietyIsVisible(variety)),
                                    modalProduct.VarietyGroups,
                                    activeVarietyGroupId
                                );
                                return (
                                    <>
                                        <VarietyGroupTabs
                                            groups={modalProduct.VarietyGroups}
                                            activeGroupId={activeVarietyGroupId}
                                            onSelect={setActiveVarietyGroupId}
                                            className="mt-4"
                                        />
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                            {visibleVarieties.map((variety) => {
                                                const allowedPrices = getAllowedPricesForVariety(variety);
                                                if (allowedPrices.length === 0) return null;
                                                const pricedEntries = allowedPrices.map((entry) => {
                                                    const unitDiscount = getUnitDiscountForVarietyPrice(variety, entry);
                                                    const display = resolveVarietyPriceDisplay(variety, entry, unitDiscount);
                                                    return {
                                                        entry,
                                                        shownPrice: Number(display?.price ?? entry.price),
                                                        taxFreeApplied: Boolean(display?.taxFreeApplied),
                                                    };
                                                });
                                                const shownPrices = pricedEntries.map((entry) => entry.shownPrice);
                                                const highestPrice = Math.max(...shownPrices);
                                                const lowestPrice = Math.min(...shownPrices);
                                                const highestPriceUnit = pricedEntries.find((entry) => entry.shownPrice === highestPrice)?.entry?.metricUnit || '';
                                                const lowestPriceUnit = pricedEntries.find((entry) => entry.shownPrice === lowestPrice)?.entry?.metricUnit || '';
                                                const hasTaxFreePrices = pricedEntries.some((entry) => entry.taxFreeApplied);
                                                const isSoldOut = isVarietySoldOut(variety);
                                                const isSelectable = !isSoldOut;

                                                return (
                                                    <div
                                                        key={variety.id}
                                                        className={`relative bg-white border rounded-lg shadow p-4 ${
                                                            isSelectable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed'
                                                        } ${
                                                            isSoldOut ? 'opacity-70' : ''
                                                        }`}
                                                        onClick={() => {
                                                            if (!isSelectable) return;
                                                            setModalVariety(variety);
                                                        }}
                                                    >
                                                        {isSoldOut && (
                                                            <span className="absolute top-2 right-2 z-10 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
                                                                Agotado
                                                            </span>
                                                        )}
                                                        <div className="flex items-center gap-4 md:gap-8 mb-2">
                                                            <img
                                                                src={variety.varietyImage || "https://placehold.co/200"}
                                                                alt={variety.varietyName}
                                                                className="w-16 h-16 object-cover rounded-lg shadow"
                                                            />
                                                            <p className="text-sm font-semibold break-words mt-2">{variety.varietyName}</p>
                                                        </div>

                                                        <div className="flex justify-between text-sm">
                                                            {highestPrice === lowestPrice ? (
                                                                <p className="text-gray-700 text-sm mt-1 inline-flex items-center">
                                                                    ${formatPesoAmount(highestPrice)} {highestPriceUnit}
                                                                    {hasTaxFreePrices && (
                                                                        <span
                                                                            className="ml-1 inline-flex text-blue-700"
                                                                            title="Precio ajustado sin impuestos excluidos."
                                                                            aria-label="Precio ajustado sin impuestos excluidos."
                                                                        >
                                                                            <FiInfo size={13} />
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            ) : allowedPrices.length > 0 && (
                                                                <>
                                                                    <p className="text-gray-700 text-sm mt-1 inline-flex items-center">
                                                                        ${formatPesoAmount(highestPrice)} {highestPriceUnit}
                                                                        {hasTaxFreePrices && (
                                                                            <span
                                                                                className="ml-1 inline-flex text-blue-700"
                                                                                title="Precio ajustado sin impuestos excluidos."
                                                                                aria-label="Precio ajustado sin impuestos excluidos."
                                                                            >
                                                                                <FiInfo size={13} />
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                    <p className="text-gray-700 text-sm mt-1 inline-flex items-center">
                                                                        ${formatPesoAmount(lowestPrice)} {lowestPriceUnit}
                                                                        {hasTaxFreePrices && (
                                                                            <span
                                                                                className="ml-1 inline-flex text-blue-700"
                                                                                title="Precio ajustado sin impuestos excluidos."
                                                                                aria-label="Precio ajustado sin impuestos excluidos."
                                                                            >
                                                                                <FiInfo size={13} />
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {visibleVarieties.length === 0 && (
                                            <p className="mt-4 text-center text-sm text-gray-500">
                                                No hay variedades para este grupo.
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );

};

export default QuotationBuilderPage;
